import { useState, useEffect, useMemo, useCallback } from 'react'
import InventoryService from '../services/InventoryService'
import { exportToCSV } from '../utils/exportCSV'
import {
  Package, Plus, Trash2, Edit3, Save, X, Search, AlertTriangle,
  TrendingDown, TrendingUp, DollarSign, Box, Layers, History, Wrench,
  ArrowDownCircle, ArrowUpCircle, CheckCircle, BarChart2, Tag, Download, Truck, ClipboardList, Package2,
  RefreshCw, Pencil, Globe, ExternalLink, Loader
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import './InventoryHub.css'

const DEFAULT_DEVICE_MODELS = ['GP-BOX', 'GP-CAM', 'GP-DIM', 'GP-HIDRO', 'GP-MINI', 'GP-MINI-HUB', 'GP-RELAY', 'GP-SOIL']
const CATEGORIES = ['microcontroller', 'sensor', 'relay', 'pcb', 'case', 'power', 'cable', 'connector', 'general']
const PRODUCT_CATEGORIES = ['dispositivo', 'suscripcion', 'bomba', 'sensor', 'nutriente', 'accesorio', 'iluminacion', 'ventilacion', 'riego', 'medicion', 'sustrato', 'otro']
const SUPPLIER_CATEGORIES = ['electrónica', 'sensores', 'insumos', 'packaging', 'logística', 'servicios', 'general']
const TAX_CATEGORIES = ['exento', 'iva_21', 'iva_10_5']

function formatCurrency(n) {
  if (n == null) return '$0'
  return '$' + Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

export default function InventoryHub() {
  const [tab, setTab] = useState('dashboard')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Data
  const [dashboard, setDashboard] = useState(null)
  const [components, setComponents] = useState([])
  const [bom, setBom] = useState([])
  const [subBoms, setSubBoms] = useState([])
  const [entries, setEntries] = useState([])
  const [movements, setMovements] = useState([])
  const [products, setProducts] = useState([])
  const [stockItems, setStockItems] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [deviceModels, setDeviceModels] = useState([])

  // UI state
  const [searchTerm, setSearchTerm] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({})
  const [processing, setProcessing] = useState(false)
  const [bomModel, setBomModel] = useState('GP-BOX')
  const [deductModal, setDeductModal] = useState({ show: false, model: '', mac: '', subBomId: null, qty: 1, excludedSubBoms: new Set() })

  const DEVICE_MODELS = useMemo(() => {
    const dbNames = deviceModels.map(d => d.name)
    const merged = [...new Set([...DEFAULT_DEVICE_MODELS, ...dbNames])]
    return merged.sort()
  }, [deviceModels])

  const deviceModelPrices = useMemo(() => {
    const map = {}
    deviceModels.forEach(d => {
      if (d.salePrice > 0) {
        const key = d.name || d.id
        if (key) map[key] = d.salePrice
      }
    })
    return map
  }, [deviceModels])

  // ── Data Loading ─────────────────────────────────────

  const loadDeviceModels = useCallback(async () => {
    try {
      const res = await InventoryService.listDeviceModels()
      if (res.success) setDeviceModels(res.deviceModels || [])
    } catch (e) { console.error('Device models load error:', e) }
  }, [])

  const loadDashboard = useCallback(async () => {
    try {
      const res = await InventoryService.getDashboard()
      if (res.success) setDashboard(res)
    } catch (e) { console.error('Dashboard load error:', e) }
  }, [])

  const loadComponents = useCallback(async () => {
    try {
      const res = await InventoryService.listComponents()
      if (res.success) setComponents(res.components || [])
    } catch (e) { console.error('Components load error:', e) }
  }, [])

  const loadBOM = useCallback(async () => {
    try {
      const res = await InventoryService.listBOM()
      if (res.success) setBom(res.bom || [])
    } catch (e) { console.error('BOM load error:', e) }
  }, [])

  const loadSubBoms = useCallback(async () => {
    try {
      const res = await InventoryService.listSubBOMs()
      if (res.success) setSubBoms(res.subBoms || [])
    } catch (e) { console.error('SubBoms load error:', e) }
  }, [])

  const loadEntries = useCallback(async () => {
    try {
      const res = await InventoryService.listEntries()
      if (res.success) setEntries(res.entries || [])
    } catch (e) { console.error('Entries load error:', e) }
  }, [])

  const loadMovements = useCallback(async () => {
    try {
      const res = await InventoryService.listMovements()
      if (res.success) setMovements(res.movements || [])
    } catch (e) { console.error('Movements load error:', e) }
  }, [])

  const loadProducts = useCallback(async () => {
    try {
      const res = await InventoryService.listProducts()
      if (res.success) setProducts(res.products || [])
    } catch (e) { console.error('Products load error:', e) }
  }, [])

  const loadStockItems = useCallback(async () => {
    try {
      const res = await InventoryService.listStockItems()
      if (res.success) setStockItems(res.stockItems || [])
    } catch (e) { console.error('StockItems load error:', e) }
  }, [])

  const loadSuppliers = useCallback(async () => {
    try {
      const res = await InventoryService.listSuppliers()
      if (res.success) setSuppliers(res.suppliers || [])
    } catch (e) { console.error('Suppliers load error:', e) }
  }, [])

  const loadPurchaseOrders = useCallback(async () => {
    try {
      const res = await InventoryService.listPurchaseOrders()
      if (res.success) setPurchaseOrders(res.purchaseOrders || [])
    } catch (e) { console.error('PO load error:', e) }
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await Promise.all([loadDashboard(), loadComponents(), loadBOM(), loadSubBoms(), loadEntries(), loadMovements(), loadProducts(), loadSuppliers(), loadPurchaseOrders(), loadDeviceModels()])
    } catch (e) {
      setError('Error cargando inventario')
    } finally {
      setLoading(false)
    }
  }, [loadDashboard, loadComponents, loadBOM, loadSubBoms, loadEntries, loadMovements, loadProducts, loadSuppliers, loadPurchaseOrders, loadDeviceModels])

  useEffect(() => { loadAll() }, [loadAll])

  // Reload relevant data when switching tabs
  useEffect(() => {
    if (tab === 'dashboard') { loadDashboard(); loadStockItems(); loadDeviceModels() }
    if (tab === 'components') loadComponents()
    if (tab === 'bom') { loadBOM(); loadSubBoms(); loadDeviceModels() }
    if (tab === 'entries') loadEntries()
    if (tab === 'movements') loadMovements()
    if (tab === 'products') loadProducts()
    if (tab === 'disponible') loadStockItems()
    if (tab === 'purchase-orders') loadPurchaseOrders()
  }, [tab, loadDashboard, loadComponents, loadBOM, loadEntries, loadMovements, loadProducts, loadSuppliers, loadPurchaseOrders])

  // ── Component CRUD ───────────────────────────────────

  const handleCreateComponent = async () => {
    if (!formData.name) return
    setProcessing(true)
    try {
      await InventoryService.createComponent(formData)
      setShowForm(false)
      setFormData({})
      await loadComponents()
      await loadDashboard()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleUpdateComponent = async () => {
    setProcessing(true)
    try {
      await InventoryService.updateComponent(editingId, formData)
      setEditingId(null)
      setFormData({})
      await loadComponents()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteComponent = async (id) => {
    if (!confirm('¿Eliminar este componente?')) return
    setProcessing(true)
    try {
      await InventoryService.deleteComponent(id)
      await loadComponents()
      await loadDashboard()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── BOM CRUD ─────────────────────────────────────────

  const handleCreateBOM = async () => {
    const isSubBom = !!formData.subBomId
    if (isSubBom) {
      if (!formData.subBomId || !formData.quantity) return
    } else {
      if (!formData.componentId || !formData.quantity) return
    }
    setProcessing(true)
    try {
      const payload = { deviceModel: bomModel, quantity: Number(formData.quantity) }
      if (isSubBom) {
        payload.subBomId = formData.subBomId
        payload.variantIds = formData.variantIds || []
      } else {
        payload.componentId = formData.componentId
      }
      if (payload.subBomId && formData.maxQuantity && Number(formData.maxQuantity) > Number(formData.quantity)) {
        payload.maxQuantity = Number(formData.maxQuantity)
      }
      await InventoryService.createBOMEntry(payload)
      setShowForm(false)
      setFormData({})
      await loadBOM()
      await loadDashboard()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── Sub-BOM CRUD ──────────────────────────────────────

  const handleCreateSubBOM = async (data) => {
    try {
      await InventoryService.createSubBOM(data)
      await loadSubBoms()
    } catch (e) { setError(e.message) }
  }

  const handleUpdateSubBOM = async (id, data) => {
    try {
      await InventoryService.updateSubBOM(id, data)
      await loadSubBoms()
    } catch (e) { setError(e.message) }
  }

  const handleDeleteSubBOM = async (id) => {
    if (!confirm('¿Eliminar este Sub-BOM?')) return
    try {
      await InventoryService.deleteSubBOM(id)
      await loadSubBoms()
    } catch (e) { setError(e.message) }
  }

  const handleAssembleSubBOM = async (id, qty) => {
    try {
      const res = await InventoryService.assembleSubBOM(id, qty)
      if (!res.success) throw new Error(res.error || 'Error al ensamblar')
      await Promise.all([loadSubBoms(), loadComponents(), loadMovements(), loadDashboard()])
      return res
    } catch (e) { setError(e.message); throw e }
  }

  const handleDeleteBOM = async (id) => {
    if (!confirm('¿Eliminar esta entrada del BOM?')) return
    setProcessing(true)
    try {
      await InventoryService.deleteBOMEntry(id)
      await loadBOM()
      await loadDashboard()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── Stock Entry ──────────────────────────────────────

  const handleCreateEntry = async () => {
    if (!formData.componentId || !formData.quantity || formData.unitCost === undefined) return
    setProcessing(true)
    try {
      await InventoryService.createEntry(formData)
      setShowForm(false)
      setFormData({})
      await loadEntries()
      await loadComponents()
      await loadDashboard()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteEntry = async (id) => {
    if (!confirm('¿Eliminar esta entrada de stock?')) return
    setProcessing(true)
    try {
      await InventoryService.deleteEntry(id)
      await loadEntries()
      await loadComponents()
      await loadDashboard()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── Products CRUD ────────────────────────────────────

  const handleCreateProduct = async () => {
    if (!formData.name || !formData.salePrice) return
    setProcessing(true)
    try {
      await InventoryService.createProduct(formData)
      setShowForm(false)
      setFormData({})
      await loadProducts()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleUpdateProduct = async () => {
    setProcessing(true)
    try {
      await InventoryService.updateProduct(editingId, formData)
      setEditingId(null)
      setFormData({})
      await loadProducts()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteProduct = async (id) => {
    if (!confirm('¿Eliminar este producto?')) return
    setProcessing(true)
    try {
      await InventoryService.deleteProduct(id)
      await loadProducts()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── Suppliers CRUD ───────────────────────────────────

  const handleCreateSupplier = async () => {
    if (!formData.name) return
    setProcessing(true)
    try {
      await InventoryService.createSupplier(formData)
      setShowForm(false)
      setFormData({})
      await loadSuppliers()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleUpdateSupplier = async () => {
    setProcessing(true)
    try {
      await InventoryService.updateSupplier(editingId, formData)
      setEditingId(null)
      setFormData({})
      await loadSuppliers()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteSupplier = async (id) => {
    if (!confirm('¿Eliminar este proveedor?')) return
    setProcessing(true)
    try {
      await InventoryService.deleteSupplier(id)
      await loadSuppliers()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── Purchase Orders CRUD ─────────────────────────────────

  const [poFormData, setPoFormData] = useState({ items: [] })
  const [showPOForm, setShowPOForm] = useState(false)

  const handleCreatePO = async () => {
    if (!poFormData.supplierId || poFormData.items.length === 0) return
    setProcessing(true)
    try {
      await InventoryService.createPurchaseOrder({
        ...poFormData,
        items: JSON.stringify(poFormData.items),
      })
      setShowPOForm(false)
      setPoFormData({ items: [] })
      await loadPurchaseOrders()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleSendPO = async (id) => {
    setProcessing(true)
    try {
      await InventoryService.sendPurchaseOrder(id)
      await loadPurchaseOrders()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleReceivePO = async (id) => {
    if (!confirm('¿Confirmar recepción? Esto ingresará los items al stock.')) return
    setProcessing(true)
    try {
      await InventoryService.receivePurchaseOrder(id)
      await loadPurchaseOrders()
      await loadEntries()
      await loadComponents()
      await loadDashboard()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleCancelPO = async (id) => {
    if (!confirm('¿Cancelar esta orden de compra?')) return
    setProcessing(true)
    try {
      await InventoryService.cancelPurchaseOrder(id)
      await loadPurchaseOrders()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  const handleDeletePO = async (id) => {
    if (!confirm('¿Eliminar esta orden de compra?')) return
    setProcessing(true)
    try {
      await InventoryService.deletePurchaseOrder(id)
      await loadPurchaseOrders()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── Deduct Device ────────────────────────────────────

  const handleDeduct = async () => {
    if (!deductModal.model) return
    setProcessing(true)
    try {
      let res
      if (deductModal.subBomId) {
        res = await InventoryService.deductSubBom(
          deductModal.subBomId,
          deductModal.mac,
          deductModal.qty || 1,
          'subbom_fabrication'
        )
      } else {
        const excludedSubBoms = [...(deductModal.excludedSubBoms || [])]
        res = await InventoryService.deductDevice(
          deductModal.model,
          deductModal.mac,
          'device_registration',
          excludedSubBoms.length > 0 ? excludedSubBoms : undefined
        )
      }
      if (res.hasShortfall) {
        alert(`⚠️ Stock insuficiente en algunos componentes. Costo parcial: ${formatCurrency(res.totalCost)}`)
      } else {
        const label = deductModal.subBomId ? `Sub-BOM \u00d7${deductModal.qty || 1}` : 'dispositivo'
        alert(`✅ Deducción exitosa. Costo real del ${label}: ${formatCurrency(res.totalCost)}`)
      }
      setDeductModal({ show: false, model: '', mac: '', subBomId: null, qty: 1, excludedSubBoms: new Set() })
      await loadAll()
    } catch (e) { setError(e.message) }
    finally { setProcessing(false) }
  }

  // ── Filtered data ────────────────────────────────────

  const filteredComponents = useMemo(() => {
    if (!searchTerm) return components
    const s = searchTerm.toLowerCase()
    return components.filter(c =>
      c.name?.toLowerCase().includes(s) ||
      c.sku?.toLowerCase().includes(s) ||
      c.category?.toLowerCase().includes(s)
    )
  }, [components, searchTerm])

  const filteredBOM = useMemo(() => {
    return bom.filter(b => b.deviceModel === bomModel)
  }, [bom, bomModel])

  const compMap = useMemo(() => {
    const m = {}
    for (const c of components) m[c.$id] = c
    return m
  }, [components])

  // Avg cost per component: from dashboard bomSummary (all models) + fallback from entries
  const compAvgCostMap = useMemo(() => {
    const map = {}
    if (dashboard?.bomSummary) {
      Object.values(dashboard.bomSummary).forEach(items => {
        items.forEach(item => {
          if (item.componentId && item.avgCost != null) map[item.componentId] = item.avgCost
        })
      })
    }
    // Fallback: compute from entries for components not in bomSummary
    const byComp = {}
    for (const e of entries) {
      if (!e.componentId) continue
      if (!byComp[e.componentId]) byComp[e.componentId] = []
      byComp[e.componentId].push(e)
    }
    for (const [cid, ents] of Object.entries(byComp)) {
      if (map[cid] != null) continue
      const active = ents.filter(e => (e.remainingQty || 0) > 0)
      if (active.length === 0) continue
      const totalQty = active.reduce((s, e) => s + (e.remainingQty || 0), 0)
      const totalCost = active.reduce((s, e) => s + (e.unitCost || 0) * (e.remainingQty || 0), 0)
      if (totalQty > 0) map[cid] = totalCost / totalQty
    }
    return map
  }, [dashboard, entries])

  // Price trends: per component, compare last 2 entries' unitCost
  const priceTrends = useMemo(() => {
    const byComp = {}
    // Group entries by componentId, sorted by date desc
    for (const e of entries) {
      const cid = e.componentId
      if (!byComp[cid]) byComp[cid] = []
      byComp[cid].push(e)
    }
    const trends = {}
    for (const [cid, ents] of Object.entries(byComp)) {
      const sorted = [...ents].sort((a, b) => {
        const da = new Date(a.entryDate || a.$createdAt || 0).getTime()
        const db = new Date(b.entryDate || b.$createdAt || 0).getTime()
        return db - da // newest first
      })
      const latest = Number(sorted[0]?.unitCost || 0)
      const prev = sorted.length > 1 ? Number(sorted[1]?.unitCost || 0) : null
      trends[cid] = {
        latest,
        prev,
        diff: prev != null ? latest - prev : null,
        pct: prev != null && prev > 0 ? ((latest - prev) / prev) * 100 : null,
        entries: sorted,
      }
    }
    return trends
  }, [entries])

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products
    const s = searchTerm.toLowerCase()
    return products.filter(p =>
      p.name?.toLowerCase().includes(s) ||
      p.sku?.toLowerCase().includes(s) ||
      p.category?.toLowerCase().includes(s)
    )
  }, [products, searchTerm])

  const filteredSuppliers = useMemo(() => {
    if (!searchTerm) return suppliers
    const s = searchTerm.toLowerCase()
    return suppliers.filter(sup =>
      sup.name?.toLowerCase().includes(s) ||
      sup.contactName?.toLowerCase().includes(s) ||
      sup.email?.toLowerCase().includes(s) ||
      sup.category?.toLowerCase().includes(s)
    )
  }, [suppliers, searchTerm])

  // ── Render Helpers ───────────────────────────────────

  if (loading) {
    return (
      <div className="inv-page">
        <div className="inv-loading">
          <Package size={48} className="spin" />
          <p>Cargando inventario...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="inv-page">
      {/* Header */}
      <div className="inv-header">
        <div className="inv-header-left">
          <Package size={28} />
          <div>
            <h1>Inventario</h1>
            <p className="inv-subtitle">Gestión de stock, componentes y costos FIFO</p>
          </div>
        </div>
        <div className="inv-header-actions">
          <button className="inv-btn" onClick={() => {
            const dateStr = new Date().toISOString().split('T')[0]
            if (tab === 'products') {
              exportToCSV(products, [
                { key: 'name', label: 'Nombre' },
                { key: 'sku', label: 'SKU' },
                { key: 'category', label: 'Categoría' },
                { key: 'costPrice', label: 'Costo' },
                { key: 'salePrice', label: 'Precio Venta' },
                { key: 'stock', label: 'Stock' },
                { key: 'minStock', label: 'Stock Mín' },
                { key: 'unit', label: 'Unidad' },
                { key: 'taxCategory', label: 'Categoría Fiscal' },
              ], `productos_${dateStr}`)
            } else {
              exportToCSV(components, [
                { key: 'name', label: 'Componente' },
                { key: 'category', label: 'Categoría' },
                { key: 'currentStock', label: 'Stock' },
                { key: 'minStock', label: 'Stock Mín' },
                { key: 'avgCost', label: 'Costo Promedio', format: (v) => v != null ? Number(v).toFixed(2) : '' },
                { key: 'totalValue', label: 'Valor Total' },
              ], `componentes_${dateStr}`)
            }
          }}>
            <Download size={16} /> Exportar CSV
          </button>
          <button className="inv-btn inv-btn-accent" onClick={() => setDeductModal({ show: true, model: 'GP-BOX', mac: '', subBomId: null, qty: 1, excludedSubBoms: new Set() })}>
            <ArrowDownCircle size={16} /> Deducir Stock
          </button>
        </div>
      </div>

      {error && (
        <div className="inv-error">
          <AlertTriangle size={16} /> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="inv-tabs">
        {[
          { key: 'dashboard', label: 'Dashboard', icon: BarChart2 },
          { key: 'components', label: 'Componentes', icon: Box },
          { key: 'bom', label: 'BOM', icon: Layers },
          { key: 'entries', label: 'Entradas', icon: ArrowUpCircle },
          { key: 'movements', label: 'Movimientos', icon: History },
          { key: 'disponible', label: 'Disponible', icon: Package2 },
          { key: 'products', label: 'Productos', icon: Tag },
          { key: 'purchase-orders', label: 'Órdenes Compra', icon: ClipboardList },
          { key: 'supplier-search', label: 'Buscador Precios', icon: Globe },
        ].map(t => (
          <button
            key={t.key}
            className={`inv-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => { setTab(t.key); setShowForm(false); setEditingId(null) }}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="inv-content">
        {tab === 'dashboard' && <DashboardTab dashboard={dashboard} components={components} products={products} priceTrends={priceTrends} entries={entries} bom={bom} subBoms={subBoms} compMap={compMap} movements={movements} stockItems={stockItems} modelPrices={deviceModelPrices} />}
        {tab === 'components' && (
          <ComponentsTab
            components={filteredComponents}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            showForm={showForm}
            setShowForm={setShowForm}
            editingId={editingId}
            setEditingId={setEditingId}
            formData={formData}
            setFormData={setFormData}
            processing={processing}
            onCreate={handleCreateComponent}
            onUpdate={handleUpdateComponent}
            onDelete={handleDeleteComponent}
            priceTrends={priceTrends}
          />
        )}
        {tab === 'bom' && (
          <BOMTab
            bom={filteredBOM}
            bomModel={bomModel}
            setBomModel={setBomModel}
            deviceModels={DEVICE_MODELS}
            components={components}
            compMap={compMap}
            showForm={showForm}
            setShowForm={setShowForm}
            formData={formData}
            setFormData={setFormData}
            processing={processing}
            onCreate={handleCreateBOM}
            onDelete={handleDeleteBOM}
            dashboard={dashboard}
            onAddModel={async (name) => {
              await InventoryService.createDeviceModel(name)
              await loadDeviceModels()
            }}
            onDeleteModel={async (id) => {
              await InventoryService.deleteDeviceModel(id)
              await loadDeviceModels()
            }}
            modelPrices={deviceModelPrices}
            onUpdateModelPrice={async (modelId, price) => {
              await InventoryService.updateDeviceModel(modelId, { salePrice: price })
              await loadDeviceModels()
            }}
            subBoms={subBoms}
            onCreateSubBOM={handleCreateSubBOM}
            onUpdateSubBOM={handleUpdateSubBOM}
            onDeleteSubBOM={handleDeleteSubBOM}
            onAssembleSubBOM={handleAssembleSubBOM}
            compAvgCostMap={compAvgCostMap}
          />
        )}
        {tab === 'entries' && (
          <EntriesTab
            entries={entries}
            compMap={compMap}
            components={components}
            showForm={showForm}
            setShowForm={setShowForm}
            formData={formData}
            setFormData={setFormData}
            processing={processing}
            onCreate={handleCreateEntry}
            onDelete={handleDeleteEntry}
            priceTrends={priceTrends}
          />
        )}
        {tab === 'movements' && (
          <MovementsTab
            movements={movements}
            compMap={compMap}
            components={components}
            DEVICE_MODELS={DEVICE_MODELS}
            onManualMovement={async (form) => {
              await InventoryService.createManualMovement(form)
              await loadMovements()
              await loadComponents()
              if (tab === 'dashboard') await loadDashboard()
            }}
            onDeleteMovement={async (id) => {
              await InventoryService.deleteMovement(id)
              await loadMovements()
            }}
          />
        )}
        {tab === 'disponible' && (
          <StockItemsTab
            stockItems={stockItems}
            modelPrices={deviceModelPrices}
            onDelete={async (id) => {
              if (!window.confirm('¿Eliminar este dispositivo de stock? Se revertirán los movimientos de inventario y se restaurará el stock de componentes.')) return
              await InventoryService.deleteStockItem(id)
              await loadStockItems()
              await loadMovements()
              await loadComponents()
            }}
            onUpdateStatus={async (id, status) => {
              await InventoryService.updateStockItem(id, { status })
              await loadStockItems()
            }}
            onBackfill={async () => {
              const res = await InventoryService.backfillStockItems()
              await loadStockItems()
              return res
            }}
          />
        )}
        {tab === 'products' && (
          <ProductsTab
            products={filteredProducts}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            showForm={showForm}
            setShowForm={setShowForm}
            editingId={editingId}
            setEditingId={setEditingId}
            formData={formData}
            setFormData={setFormData}
            processing={processing}
            onCreate={handleCreateProduct}
            onUpdate={handleUpdateProduct}
            onDelete={handleDeleteProduct}
          />
        )}
        {tab === 'purchase-orders' && (
          <PurchaseOrdersTab
            purchaseOrders={purchaseOrders}
            suppliers={suppliers}
            components={components}
            showForm={showPOForm}
            setShowForm={setShowPOForm}
            formData={poFormData}
            setFormData={setPoFormData}
            processing={processing}
            onCreate={handleCreatePO}
            onSend={handleSendPO}
            onReceive={handleReceivePO}
            onCancel={handleCancelPO}
            onDelete={handleDeletePO}
          />
        )}
        {tab === 'supplier-search' && (
          <SupplierSearchTab components={components} bom={bom} bomModel={bomModel} compMap={compMap} />
        )}
      </div>

      {/* Deduct Modal */}
      {deductModal.show && (() => {
        const closeModal = () => setDeductModal({ show: false, model: '', mac: '', subBomId: null, qty: 1, excludedSubBoms: new Set() })
        const excluded = deductModal.excludedSubBoms || new Set()
        const isSubBomMode = !!deductModal.subBomId

        // ── Device mode vars ──
        const modelBOM = isSubBomMode ? [] : bom.filter(b => b.deviceModel === deductModal.model)
        const subBomEntries = modelBOM.filter(b => b.subBomId)
        const directSummary = isSubBomMode ? [] : (dashboard?.bomSummary?.[deductModal.model] || []).filter(b => !b.isSubBom)

        const directCost = directSummary.reduce((s, b) => s + (b.lineCost || 0), 0)
        const subBomCostEst = subBomEntries.reduce((total, entry) => {
          if (excluded.has(entry.subBomId)) return total
          const sb = subBoms.find(s => s.$id === entry.subBomId)
          if (!sb?.components) return total
          const multiplier = entry.quantity || 1
          return total + sb.components.reduce((s, c) => s + (compAvgCostMap[c.componentId] || 0) * (c.quantity || 1) * multiplier, 0)
        }, 0)

        // ── Sub-BOM mode vars ──
        const activeSb = isSubBomMode ? subBoms.find(s => s.$id === deductModal.subBomId) : null
        const sbQty = deductModal.qty || 1
        const sbEstCost = activeSb
          ? (activeSb.components || []).reduce((s, c) => s + (compAvgCostMap[c.componentId] || 0) * (c.quantity || 1) * sbQty, 0)
          : 0

        const estimatedCost = isSubBomMode ? sbEstCost : directCost + subBomCostEst

        const toggleSubBom = (subBomId) => {
          setDeductModal(p => {
            const next = new Set(p.excludedSubBoms || [])
            if (next.has(subBomId)) next.delete(subBomId)
            else next.add(subBomId)
            return { ...p, excludedSubBoms: next }
          })
        }

        return (
          <div className="inv-modal-overlay" onClick={closeModal}>
            <div className="inv-modal" onClick={e => e.stopPropagation()}>
              <div className="inv-modal-header">
                <h3><ArrowDownCircle size={20} /> Deducir Stock</h3>
                <button onClick={closeModal}><X size={18} /></button>
              </div>
              <div className="inv-modal-body">
                <p className="inv-modal-desc">Deducirá los componentes del BOM del stock usando FIFO.</p>
                <div className="inv-form-group">
                  <label>Modelo / Sub-BOM</label>
                  <select value={deductModal.model} onChange={e => {
                    const val = e.target.value
                    const sbId = val.startsWith('__sb__') ? val.slice(6) : null
                    setDeductModal(p => ({ ...p, model: val, subBomId: sbId, qty: 1, excludedSubBoms: new Set() }))
                  }}>
                    <optgroup label="Dispositivos">
                      {DEVICE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                    </optgroup>
                    {subBoms.length > 0 && (
                      <optgroup label="Sub-BOMs">
                        {subBoms.map(sb => <option key={sb.$id} value={`__sb__${sb.$id}`}>[Sub-BOM] {sb.name}</option>)}
                      </optgroup>
                    )}
                  </select>
                </div>

                {isSubBomMode && (
                  <div className="inv-form-group">
                    <label>Cantidad a fabricar</label>
                    <input
                      type="number" min="1" step="1"
                      value={deductModal.qty || 1}
                      onChange={e => setDeductModal(p => ({ ...p, qty: Math.max(1, parseInt(e.target.value) || 1) }))}
                    />
                  </div>
                )}

                <div className="inv-form-group">
                  <label>MAC del dispositivo (opcional)</label>
                  <input
                    type="text"
                    placeholder="XX:XX:XX:XX:XX:XX"
                    value={deductModal.mac}
                    onChange={e => setDeductModal(p => ({ ...p, mac: e.target.value }))}
                  />
                </div>

                {/* Sub-BOM mode: flat component list */}
                {isSubBomMode && activeSb && (
                  <div className="inv-deduct-preview">
                    <h4>Componentes a deducir (×{sbQty}):</h4>
                    {(activeSb.components || []).map((c, i) => {
                      const comp = compMap[c.componentId]
                      const needed = (c.quantity || 1) * sbQty
                      const stock = comp?.currentStock || 0
                      return (
                        <div key={i} className="inv-deduct-line">
                          <span>{comp?.name || c.componentId}</span>
                          <span>×{needed}</span>
                          <span className={stock >= needed ? 'ok' : 'low'}>Stock: {stock}</span>
                        </div>
                      )
                    })}
                    <div className="inv-deduct-total">
                      Costo estimado: {formatCurrency(estimatedCost)}
                    </div>
                  </div>
                )}

                {/* Device mode: direct + sub-BOM toggles */}
                {!isSubBomMode && (directSummary.length > 0 || subBomEntries.length > 0) && (
                  <div className="inv-deduct-preview">
                    <h4>Componentes a deducir:</h4>

                    {directSummary.map((b, i) => (
                      <div key={i} className="inv-deduct-line">
                        <span>{b.componentName}</span>
                        <span>×{b.quantity}</span>
                        <span className={b.stock >= b.quantity ? 'ok' : 'low'}>Stock: {b.stock}</span>
                      </div>
                    ))}

                    {subBomEntries.length > 0 && (
                      <>
                        {directSummary.length > 0 && <div className="inv-deduct-divider" />}
                        {subBomEntries.map((entry) => {
                          const sb = subBoms.find(s => s.$id === entry.subBomId)
                          const isIncluded = !excluded.has(entry.subBomId)
                          const multiplier = entry.quantity || 1
                          return (
                            <div key={entry.$id} className="inv-deduct-subbom-group">
                              <div
                                className={`inv-deduct-subbom-toggle ${isIncluded ? 'included' : 'excluded'}`}
                                onClick={() => toggleSubBom(entry.subBomId)}
                              >
                                <span className={`inv-deduct-checkbox ${isIncluded ? 'checked' : ''}`}>
                                  {isIncluded ? '✓' : ''}
                                </span>
                                <span className="inv-subbom-badge"><Layers size={11} /> Sub-BOM</span>
                                <span className="inv-deduct-subbom-toggle-name">{sb?.name || entry.subBomId}</span>
                                {multiplier > 1 && <span className="inv-deduct-subbom-mult">×{multiplier}</span>}
                              </div>
                              {isIncluded && sb?.components && (
                                <div className="inv-deduct-subbom-expansion">
                                  {sb.components.map((c, ci) => {
                                    const comp = compMap[c.componentId]
                                    const neededQty = (c.quantity || 1) * multiplier
                                    const stock = comp?.currentStock || 0
                                    return (
                                      <div key={ci} className="inv-deduct-line inv-deduct-line-child">
                                        <span>{comp?.name || c.componentId}</span>
                                        <span>×{neededQty}</span>
                                        <span className={stock >= neededQty ? 'ok' : 'low'}>Stock: {stock}</span>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}

                    <div className="inv-deduct-total">
                      Costo estimado: {formatCurrency(estimatedCost)}
                    </div>
                  </div>
                )}
              </div>
              <div className="inv-modal-footer">
                <button className="inv-btn" onClick={closeModal}>Cancelar</button>
                <button className="inv-btn inv-btn-danger" onClick={handleDeduct} disabled={processing}>
                  {processing ? 'Procesando...' : 'Deducir Stock'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: DASHBOARD
// ═══════════════════════════════════════════════════════════

function DashboardTab({ dashboard, components, products, priceTrends, entries, bom, subBoms, compMap, movements, stockItems, modelPrices }) {
  if (!dashboard) return <div className="inv-empty">No hay datos de inventario aún.</div>

  const lowStockProducts = products.filter(p => p.active !== false && (p.stock || 0) <= (p.minStock || 0))

  // ── Component Demand Analysis ──
  const demandAnalysis = useMemo(() => {
    if (!bom || bom.length === 0) return []

    // Build sub-BOM lookup map
    const subBomsLookup = {}
    for (const sb of (subBoms || [])) subBomsLookup[sb.$id] = sb

    // Aggregate: for each component, which models use it and total qty per unit
    const compDemand = {}
    const addDemand = (cid, model, qty) => {
      if (!cid) return
      if (!compDemand[cid]) compDemand[cid] = { componentId: cid, models: {}, totalQtyPerUnit: 0 }
      compDemand[cid].models[model] = (compDemand[cid].models[model] || 0) + qty
      compDemand[cid].totalQtyPerUnit += qty
    }
    for (const b of bom) {
      const qty = Number(b.quantity) || 1
      if (b.subBomId) {
        // Expand sub-BOM: each sub-BOM component × BOM entry quantity (use minQty = quantity)
        const sb = subBomsLookup[b.subBomId]
        if (sb?.components) {
          for (const sc of sb.components) {
            addDemand(sc.componentId, b.deviceModel, (Number(sc.quantity) || 1) * qty)
          }
        }
      } else {
        addDemand(b.componentId, b.deviceModel, qty)
      }
    }

    // Count recent deductions (last 90 days) per component
    const now = Date.now()
    const d90 = 90 * 24 * 60 * 60 * 1000
    const recentDeductions = {}
    for (const m of (movements || [])) {
      if (m.type !== 'deduction') continue
      const ts = new Date(m.movementDate || m.$createdAt || 0).getTime()
      if (now - ts > d90) continue
      const cid = m.componentId
      recentDeductions[cid] = (recentDeductions[cid] || 0) + Math.abs(m.quantity || 0)
    }

    // Build analysis rows
    return Object.values(compDemand).map(d => {
      const comp = compMap[d.componentId]
      const stock = comp?.currentStock || 0
      const modelCount = Object.keys(d.models).length
      const consumed90d = recentDeductions[d.componentId] || 0
      const monthlyRate = consumed90d > 0 ? Math.round(consumed90d / 3) : 0
      // Recommended min: max of (monthly consumption * 2) or (totalQtyPerUnit * 5 units)
      const recMin = Math.max(monthlyRate * 2, d.totalQtyPerUnit * 5)
      const status = stock <= 0 ? 'critical' : stock < recMin ? 'low' : 'ok'
      return {
        componentId: d.componentId,
        name: comp?.name || d.componentId,
        models: d.models,
        modelCount,
        totalQtyPerUnit: d.totalQtyPerUnit,
        stock,
        currentMin: comp?.minStock || 0,
        consumed90d,
        monthlyRate,
        recMin,
        status,
      }
    })
      .sort((a, b) => {
        // Sort: critical first, then low, then by model count desc
        const prio = { critical: 0, low: 1, ok: 2 }
        if (prio[a.status] !== prio[b.status]) return prio[a.status] - prio[b.status]
        return b.modelCount - a.modelCount
      })
  }, [bom, subBoms, compMap, movements])

  // Compute previous device cost from entries for each BOM model
  const deviceCostTrends = useMemo(() => {
    if (!dashboard?.bomSummary) return {}
    const trends = {}
    for (const [model, parts] of Object.entries(dashboard.bomSummary)) {
      const currentTotal = parts.reduce((s, p) => s + (p.lineCost || 0), 0)
      // Calculate what the previous cost would have been using each component's previous entry price
      let prevTotal = 0
      let hasPrev = false
      for (const p of parts) {
        if (p.isSubBom) { prevTotal += p.lineCost || 0; continue } // sub-BOM: no price trend entries
        const t = priceTrends?.[p.componentId]
        if (t && t.prev != null) {
          prevTotal += t.prev * (p.quantity || 1)
          hasPrev = true
        } else {
          prevTotal += p.lineCost || 0
        }
      }
      const diff = hasPrev ? currentTotal - prevTotal : null
      const pct = hasPrev && prevTotal > 0 ? ((currentTotal - prevTotal) / prevTotal) * 100 : null
      trends[model] = { current: currentTotal, prev: hasPrev ? prevTotal : null, diff, pct }
    }
    return trends
  }, [dashboard, priceTrends])

  // ── Stock Valuado: available STOCK_ITEMs grouped by model ──
  const stockValuado = useMemo(() => {
    if (!stockItems?.length) return { byModel: [], totalCOGS: 0, totalRevenue: 0, totalMargin: 0 }
    const available = stockItems.filter(si => si.status === 'available')
    const byModelMap = {}
    for (const si of available) {
      const model = si.deviceModel || 'Desconocido'
      if (!byModelMap[model]) byModelMap[model] = { model, units: 0, totalCOGS: 0, totalRevenue: 0, unitPrice: modelPrices?.[model] || 0 }
      byModelMap[model].units += 1
      byModelMap[model].totalCOGS += si.productCost || 0
      // Accumulate revenue per item — each item may have a different price
      const price = si.salePrice > 0 ? si.salePrice : (modelPrices?.[model] || 0)
      byModelMap[model].totalRevenue += price
    }
    const byModel = Object.values(byModelMap).map(row => {
      const margin = row.totalRevenue > 0 ? Math.round(((row.totalRevenue - row.totalCOGS) / row.totalRevenue) * 100) : null
      return { ...row, margin }
    }).sort((a, b) => b.totalRevenue - a.totalRevenue)
    const totalCOGS = byModel.reduce((s, r) => s + r.totalCOGS, 0)
    const totalRevenue = byModel.reduce((s, r) => s + r.totalRevenue, 0)
    const totalMargin = totalRevenue > 0 ? Math.round(((totalRevenue - totalCOGS) / totalRevenue) * 100) : 0
    return { byModel, totalCOGS, totalRevenue, totalMargin }
  }, [stockItems, modelPrices])

  const stockChartData = components
    .filter(c => (c.currentStock || 0) > 0 || (c.minStock || 0) > 0)
    .map(c => ({
      name: c.name?.length > 15 ? c.name.substring(0, 15) + '…' : c.name,
      stock: c.currentStock || 0,
      min: c.minStock || 0,
    }))
    .sort((a, b) => a.stock - b.stock)

  return (
    <>
      {/* KPI Row */}
      <div className="inv-kpi-row">
        <div className="inv-kpi">
          <div className="inv-kpi-icon" style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>
            <DollarSign size={22} />
          </div>
          <div className="inv-kpi-data">
            <span className="inv-kpi-value">{formatCurrency(dashboard.stockValue)}</span>
            <span className="inv-kpi-label">Valor en Stock</span>
          </div>
        </div>
        <div className="inv-kpi">
          <div className="inv-kpi-icon" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>
            <ArrowUpCircle size={22} />
          </div>
          <div className="inv-kpi-data">
            <span className="inv-kpi-value">{formatCurrency(dashboard.totalInvested)}</span>
            <span className="inv-kpi-label">Total Invertido</span>
          </div>
        </div>
        <div className="inv-kpi">
          <div className="inv-kpi-icon" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
            <ArrowDownCircle size={22} />
          </div>
          <div className="inv-kpi-data">
            <span className="inv-kpi-value">{formatCurrency(dashboard.totalConsumed)}</span>
            <span className="inv-kpi-label">Total Consumido</span>
          </div>
        </div>
        <div className="inv-kpi">
          <div className="inv-kpi-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
            <AlertTriangle size={22} />
          </div>
          <div className="inv-kpi-data">
            <span className="inv-kpi-value">{dashboard.lowStock?.length || 0}</span>
            <span className="inv-kpi-label">Stock Bajo</span>
          </div>
        </div>
      </div>

      {/* Build Capacity + Device Costs */}
      <div className="inv-grid-2">
        <div className="inv-card">
          <h3><Wrench size={18} /> Capacidad de Armado</h3>
          <div className="inv-build-list">
            {dashboard.canBuild && Object.entries(dashboard.canBuild).length > 0 ? (
              Object.entries(dashboard.canBuild).map(([model, qty]) => (
                <div key={model} className="inv-build-item">
                  <span className="inv-build-model">{model}</span>
                  <span className={`inv-build-qty ${qty === 0 ? 'zero' : ''}`}>{qty} unidades</span>
                </div>
              ))
            ) : (
              <p className="inv-muted">Configurá el BOM para ver capacidad.</p>
            )}
          </div>
        </div>

        <div className="inv-card">
          <h3><DollarSign size={18} /> Costo Real por Modelo</h3>
          <div className="inv-build-list">
            {dashboard.bomSummary && Object.entries(dashboard.bomSummary).length > 0 ? (
              Object.entries(dashboard.bomSummary).map(([model, parts]) => {
                const total = parts.reduce((s, p) => s + (p.lineCost || 0), 0)
                const trend = deviceCostTrends[model]
                const prod = dashboard.deviceCosts?.[model]
                return (
                  <div key={model} className="inv-build-item">
                    <span className="inv-build-model">{model}</span>
                    <span className="inv-price-trend-cell">
                      <span className="inv-build-cost">{formatCurrency(total)}</span>
                      {trend && trend.diff != null && trend.diff !== 0 && (
                        <span className={`inv-trend-badge ${trend.diff > 0 ? 'up' : 'down'}`} title={`Anterior: ${formatCurrency(trend.prev)} (${trend.diff > 0 ? '+' : ''}${trend.pct.toFixed(1)}%)`}>
                          {trend.diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {trend.diff > 0 ? '+' : ''}{formatCurrency(Math.abs(trend.diff))}
                        </span>
                      )}
                    </span>
                    {prod && prod.count > 0 && (
                      <span className="inv-build-prod-info" title={`${prod.count} fabricados — Costo total ${formatCurrency(prod.cost)} — Promedio ${formatCurrency(prod.cost / prod.count)}`}>
                        {prod.count} fab. · avg {formatCurrency(prod.cost / prod.count)}
                      </span>
                    )}
                  </div>
                )
              })
            ) : (
              <p className="inv-muted">Configurá el BOM y cargá entradas.</p>
            )}
          </div>
        </div>
      </div>

      {/* Stock Valuado */}
      {stockValuado.byModel.length > 0 && (
        <div className="inv-card inv-stock-valuado-card">
          <h3><Package2 size={18} /> Stock Disponible Valuado</h3>
          <div className="inv-sv-summary">
            <div className="inv-sv-kpi">
              <span className="inv-sv-kpi-label">COGS Total</span>
              <span className="inv-sv-kpi-value">{formatCurrency(stockValuado.totalCOGS)}</span>
            </div>
            <div className="inv-sv-kpi inv-sv-kpi-revenue">
              <span className="inv-sv-kpi-label">Revenue Potencial</span>
              <span className="inv-sv-kpi-value">{stockValuado.totalRevenue > 0 ? formatCurrency(stockValuado.totalRevenue) : <span className="inv-muted">Sin precio</span>}</span>
            </div>
            <div className="inv-sv-kpi">
              <span className="inv-sv-kpi-label">Margen Bruto</span>
              <span className={`inv-sv-kpi-value ${stockValuado.totalMargin > 0 ? 'inv-sv-positive' : ''}`}>
                {stockValuado.totalRevenue > 0 ? `${stockValuado.totalMargin}%` : '—'}
              </span>
            </div>
            <div className="inv-sv-kpi">
              <span className="inv-sv-kpi-label">Ganancia Potencial</span>
              <span className="inv-sv-kpi-value inv-sv-positive">
                {stockValuado.totalRevenue > 0 ? formatCurrency(stockValuado.totalRevenue - stockValuado.totalCOGS) : '—'}
              </span>
            </div>
          </div>
          <table className="inv-table inv-sv-table">
            <thead>
              <tr>
                <th>Modelo</th>
                <th>Unidades</th>
                <th>COGS Total</th>
                <th>Precio Venta</th>
                <th>Revenue</th>
                <th>Margen</th>
              </tr>
            </thead>
            <tbody>
              {stockValuado.byModel.map(row => (
                <tr key={row.model}>
                  <td><strong>{row.model}</strong></td>
                  <td>{row.units}</td>
                  <td>{formatCurrency(row.totalCOGS)}</td>
                  <td>{row.unitPrice > 0 ? formatCurrency(row.unitPrice) : <span className="inv-muted">Sin precio</span>}</td>
                  <td>{row.totalRevenue > 0 ? formatCurrency(row.totalRevenue) : '—'}</td>
                  <td>
                    {row.margin !== null
                      ? <span style={{ color: row.margin >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>{row.margin}%</span>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stock Levels Chart */}
      {stockChartData.length > 0 && (
        <div className="inv-card inv-chart-card">
          <h3><BarChart2 size={18} /> Niveles de Stock</h3>
          <ResponsiveContainer width="100%" height={Math.max(250, stockChartData.length * 35)}>
            <BarChart data={stockChartData} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis type="number" stroke="#888" fontSize={12} />
              <YAxis type="category" dataKey="name" width={120} stroke="#888" fontSize={11} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }}
              />
              <Bar dataKey="stock" name="Stock" radius={[0, 4, 4, 0]}>
                {stockChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.stock <= entry.min ? '#ef4444' : '#3b82f6'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Component Demand Analysis */}
      {demandAnalysis.length > 0 && (
        <div className="inv-card">
          <h3><Layers size={18} /> Demanda por Componente <span className="inv-card-subtitle">— uso en BOM × consumo real</span></h3>
          <div className="inv-table-wrap">
            <table className="inv-table inv-demand-table">
              <thead>
                <tr>
                  <th>Componente</th>
                  <th>Modelos</th>
                  <th>Qty/Unidad</th>
                  <th>Stock</th>
                  <th>Consumo 90d</th>
                  <th>Tasa /mes</th>
                  <th>Min. Actual</th>
                  <th>Min. Sugerido</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {demandAnalysis.map(d => (
                  <tr key={d.componentId} className={d.status !== 'ok' ? `inv-demand-${d.status}` : ''}>
                    <td className="inv-name-cell">{d.name}</td>
                    <td>
                      <div className="inv-demand-models">
                        {Object.entries(d.models).map(([model, qty]) => (
                          <span key={model} className="inv-demand-model-badge" title={`${qty} por ${model}`}>
                            {model} ×{qty}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="inv-demand-qty">{d.totalQtyPerUnit}</td>
                    <td>
                      <span className={`inv-stock-badge ${d.status === 'critical' ? 'low' : d.status === 'low' ? 'low' : 'ok'}`}>
                        {d.stock}
                      </span>
                    </td>
                    <td>{d.consumed90d || '—'}</td>
                    <td>{d.monthlyRate || '—'}</td>
                    <td>{d.currentMin || '—'}</td>
                    <td>
                      <span className={`inv-rec-min ${d.stock < d.recMin ? 'warn' : ''}`}>{d.recMin}</span>
                    </td>
                    <td>
                      <span className={`inv-demand-status ${d.status}`}>
                        {d.status === 'critical' ? '🔴 Crítico' : d.status === 'low' ? '🟡 Bajo' : '🟢 OK'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Low Stock Alerts — Products */}
      {lowStockProducts.length > 0 && (
        <div className="inv-card inv-card-alert">
          <h3><AlertTriangle size={18} /> Alertas de Stock Bajo — Productos</h3>
          <div className="inv-alert-list">
            {lowStockProducts.map(p => (
              <div key={p.$id} className="inv-alert-item">
                <AlertTriangle size={14} />
                <span className="inv-alert-name">{p.name}</span>
                <span className="inv-alert-stock">Stock: {p.stock || 0}</span>
                <span className="inv-alert-min">Mín: {p.minStock || 0}</span>
                <span className="inv-alert-price">{formatCurrency(p.salePrice)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: COMPONENTES
// ═══════════════════════════════════════════════════════════

function ComponentsTab({
  components, searchTerm, setSearchTerm, showForm, setShowForm,
  editingId, setEditingId, formData, setFormData, processing,
  onCreate, onUpdate, onDelete, priceTrends
}) {
  return (
    <>
      <div className="inv-toolbar">
        <div className="inv-search-wrap">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar componentes..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="inv-btn inv-btn-primary" onClick={() => {
          setShowForm(!showForm)
          setEditingId(null)
          setFormData({ category: 'general', unit: 'unidad', minStock: 0 })
        }}>
          <Plus size={16} /> Nuevo Componente
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showForm || editingId) && (
        <div className="inv-form-card">
          <h4>{editingId ? 'Editar Componente' : 'Nuevo Componente'}</h4>
          <div className="inv-form-grid">
            <div className="inv-form-group">
              <label>Nombre *</label>
              <input type="text" placeholder="ESP32-WROOM-32" value={formData.name || ''}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>SKU</label>
              <input type="text" placeholder="ESP32-W32" value={formData.sku || ''}
                onChange={e => setFormData(p => ({ ...p, sku: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Categoría</label>
              <select value={formData.category || 'general'}
                onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="inv-form-group">
              <label>Unidad</label>
              <input type="text" placeholder="unidad" value={formData.unit || ''}
                onChange={e => setFormData(p => ({ ...p, unit: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Stock Mínimo</label>
              <input type="number" min="0" value={formData.minStock || 0}
                onChange={e => setFormData(p => ({ ...p, minStock: Number(e.target.value) }))} />
            </div>
            <div className="inv-form-group full-width">
              <label>Búsqueda Proveedor <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(término para buscar en proveedores)</span></label>
              <input type="text" placeholder="ej: ESP32 WROOM 32 DevKit placa desarrollo" value={formData.searchAlias || ''}
                onChange={e => setFormData(p => ({ ...p, searchAlias: e.target.value }))} />
            </div>
          </div>
          <div className="inv-form-actions">
            <button className="inv-btn" onClick={() => { setShowForm(false); setEditingId(null); setFormData({}) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="inv-btn inv-btn-primary" onClick={editingId ? onUpdate : onCreate} disabled={processing}>
              <Save size={14} /> {editingId ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>SKU</th>
              <th>Categoría</th>
              <th>Stock</th>
              <th>Mínimo</th>
              <th>Precio Unit.</th>
              <th>Unidad</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {components.length === 0 ? (
              <tr><td colSpan={8} className="inv-empty-cell">No hay componentes. Creá el primero.</td></tr>
            ) : (
              components.map(c => {
                const trend = priceTrends[c.$id]
                return (
                  <tr key={c.$id} className={(c.currentStock || 0) <= (c.minStock || 0) ? 'low-stock-row' : ''}>
                    <td className="inv-name-cell">{c.name}</td>
                    <td>{c.sku || '—'}</td>
                    <td><span className="inv-cat-badge">{c.category}</span></td>
                    <td>
                      <span className={`inv-stock-badge ${(c.currentStock || 0) <= (c.minStock || 0) ? 'low' : 'ok'}`}>
                        {c.currentStock || 0}
                      </span>
                    </td>
                    <td>{c.minStock || 0}</td>
                    <td>
                      {trend ? (
                        <span className="inv-price-trend-cell">
                          <span>{formatCurrency(trend.latest)}</span>
                          {trend.diff != null && trend.diff !== 0 && (
                            <span className={`inv-trend-badge ${trend.diff > 0 ? 'up' : 'down'}`} title={`Anterior: ${formatCurrency(trend.prev)} (${trend.diff > 0 ? '+' : ''}${trend.pct.toFixed(1)}%)`}>
                              {trend.diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              {trend.diff > 0 ? '+' : ''}{formatCurrency(Math.abs(trend.diff))}
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{c.unit || 'unidad'}</td>
                    <td className="inv-actions-cell">
                      <button className="inv-icon-btn" title="Editar" onClick={() => {
                        setEditingId(c.$id)
                        setShowForm(false)
                        setFormData({ name: c.name, sku: c.sku, category: c.category, unit: c.unit, minStock: c.minStock, searchAlias: c.searchAlias })
                      }}>
                        <Edit3 size={14} />
                      </button>
                      <button className="inv-icon-btn danger" title="Eliminar" onClick={() => onDelete(c.$id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// SUB-COMPONENT: SubBomForm
// ═══════════════════════════════════════════════════════════

function SubBomForm({ form, setForm, components, compMap, compAvgCostMap, onSave, onCancel, processing, isEdit }) {
  const totalCost = (form.components || []).reduce((sum, c) => {
    return sum + (compAvgCostMap[c.componentId] || 0) * (c.quantity || 0)
  }, 0)

  const addComponent = () => {
    setForm(f => ({ ...f, components: [...(f.components || []), { componentId: '', quantity: 1 }] }))
  }

  const removeComponent = (idx) => {
    setForm(f => ({ ...f, components: f.components.filter((_, i) => i !== idx) }))
  }

  const updateComponent = (idx, field, value) => {
    setForm(f => {
      const comps = [...f.components]
      comps[idx] = { ...comps[idx], [field]: field === 'quantity' ? Number(value) : value }
      return { ...f, components: comps }
    })
  }

  return (
    <div className="inv-subbom-form">
      <div className="inv-form-grid">
        <div className="inv-form-group">
          <label>Nombre *</label>
          <input
            type="text"
            placeholder="Ej: Sensor AHT10"
            value={form.name || ''}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div className="inv-form-group">
          <label>Descripción</label>
          <input
            type="text"
            placeholder="Descripción opcional..."
            value={form.description || ''}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>
      </div>

      <div className="inv-subbom-comp-list">
        <div className="inv-subbom-comp-list-header">
          <label>Componentes *</label>
          <button className="inv-btn inv-btn-sm" onClick={addComponent}>
            <Plus size={13} /> Agregar
          </button>
        </div>
        {(form.components || []).length === 0 && (
          <p className="inv-empty-notice">Sin componentes. Agregá al menos uno.</p>
        )}
        {(form.components || []).map((c, i) => (
          <div key={i} className="inv-subbom-comp-row">
            <select
              value={c.componentId || ''}
              onChange={e => updateComponent(i, 'componentId', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {components.map(comp => <option key={comp.$id} value={comp.$id}>{comp.name}</option>)}
            </select>
            <input
              type="number"
              min="1"
              value={c.quantity || 1}
              onChange={e => updateComponent(i, 'quantity', e.target.value)}
              className="inv-subbom-qty-input"
            />
            <button className="inv-icon-btn danger" onClick={() => removeComponent(i)}>
              <X size={12} />
            </button>
          </div>
        ))}
      </div>

      {(form.components || []).length > 0 && totalCost > 0 && (
        <div className="inv-subbom-form-total">
          Costo estimado: <strong>{formatCurrency(totalCost)}</strong>
        </div>
      )}

      <div className="inv-form-actions">
        <button className="inv-btn" onClick={onCancel}><X size={14} /> Cancelar</button>
        <button
          className="inv-btn inv-btn-primary"
          onClick={onSave}
          disabled={processing || !form.name?.trim() || (form.components || []).length === 0}
        >
          <Save size={14} /> {isEdit ? 'Guardar cambios' : 'Crear Sub-BOM'}
        </button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: BOM
// ═══════════════════════════════════════════════════════════

function BOMTab({
  bom, bomModel, setBomModel, deviceModels, components, compMap,
  showForm, setShowForm, formData, setFormData, processing,
  onCreate, onDelete, dashboard, onAddModel, onDeleteModel,
  modelPrices, onUpdateModelPrice,
  subBoms, onCreateSubBOM, onUpdateSubBOM, onDeleteSubBOM, onAssembleSubBOM, compAvgCostMap
}) {
  const [newModelName, setNewModelName] = useState('')
  const [addingModel, setAddingModel] = useState(false)
  const [editPrice, setEditPrice] = useState('')
  const [editingPrice, setEditingPrice] = useState(false)
  const [addMode, setAddMode] = useState('component') // 'component' | 'subbom'
  const [showSubBomManager, setShowSubBomManager] = useState(false)
  const [subBomForm, setSubBomForm] = useState({ name: '', description: '', components: [] })
  const [editingSubBomId, setEditingSubBomId] = useState(null)
  const [showNewSubBomForm, setShowNewSubBomForm] = useState(false)
  const [subBomProcessing, setSubBomProcessing] = useState(false)
  const [assembleForm, setAssembleForm] = useState({ subBomId: null, qty: 1, processing: false })

  const subBomsMap = useMemo(() => {
    const m = {}
    for (const sb of subBoms) m[sb.$id] = sb
    return m
  }, [subBoms])

  const calcSubBomCost = useCallback((subBomId) => {
    const sb = subBomsMap[subBomId]
    if (!sb || !sb.components) return 0
    return sb.components.reduce((total, c) => total + (compAvgCostMap[c.componentId] || 0) * (c.quantity || 0), 0)
  }, [subBomsMap, compAvgCostMap])

  const calcSubBomBuilds = useCallback((subBomId) => {
    const sb = subBomsMap[subBomId]
    if (!sb || !sb.components || sb.components.length === 0) return 0
    let minBuilds = Infinity
    for (const c of sb.components) {
      const stock = compMap[c.componentId]?.currentStock || 0
      const builds = Math.floor(stock / (c.quantity || 1))
      if (builds < minBuilds) minBuilds = builds
    }
    return minBuilds === Infinity ? 0 : minBuilds
  }, [subBomsMap, compMap])

  const modelBomSummary = dashboard?.bomSummary?.[bomModel] || []

  // Price range for the whole BOM
  const [bomMinCost, bomMaxCost] = useMemo(() => {
    let min = 0, max = 0
    for (const b of bom) {
      if (b.subBomId) {
        const allVariants = [b.subBomId, ...(b.variantIds || [])]
        const unitCosts = allVariants.map(vid => calcSubBomCost(vid))
        const minQty = b.quantity || 1
        const maxQty = b.maxQuantity && b.maxQuantity > minQty ? b.maxQuantity : minQty
        min += Math.min(...unitCosts) * minQty
        max += Math.max(...unitCosts) * maxQty
      } else if (b.componentId) {
        const summary = modelBomSummary.find(s => s.componentId === b.componentId)
        const lineCost = summary?.lineCost || 0
        min += lineCost
        max += lineCost
      }
    }
    return [min, max]
  }, [bom, modelBomSummary, calcSubBomCost])

  const hasRange = bomMinCost !== bomMaxCost
  const canBuild = dashboard?.canBuild?.[bomModel] ?? '—'

  const handleSaveSubBom = async () => {
    if (!subBomForm.name.trim() || (subBomForm.components || []).length === 0) return
    setSubBomProcessing(true)
    try {
      if (editingSubBomId) {
        await onUpdateSubBOM(editingSubBomId, subBomForm)
        setEditingSubBomId(null)
      } else {
        await onCreateSubBOM(subBomForm)
        setShowNewSubBomForm(false)
      }
      setSubBomForm({ name: '', description: '', components: [] })
    } finally {
      setSubBomProcessing(false)
    }
  }

  const handleAddModel = async () => {
    if (!newModelName.trim()) return
    setAddingModel(false)
    await onAddModel(newModelName.trim())
    setNewModelName('')
  }

  const handleDeleteModel = async (model) => {
    if (!confirm(`¿Eliminar el modelo "${model}"? Las entradas BOM asociadas no se borrarán.`)) return
    if (bomModel === model) setBomModel(deviceModels[0] || 'GP-BOX')
    await onDeleteModel(model)
  }

  return (
    <>
      <div className="inv-toolbar">
        <div className="inv-model-selector">
          {deviceModels.map(m => (
            <button key={m} className={`inv-model-btn ${bomModel === m ? 'active' : ''}`}
              onClick={() => setBomModel(m)}>
              {m}
              {!DEFAULT_DEVICE_MODELS.includes(m) && (
                <span className="inv-model-delete" onClick={e => { e.stopPropagation(); handleDeleteModel(m) }}
                  title="Eliminar modelo">×</span>
              )}
            </button>
          ))}
          {addingModel ? (
            <div className="inv-model-add-form">
              <input
                type="text"
                placeholder="Nombre del modelo..."
                value={newModelName}
                onChange={e => setNewModelName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddModel(); if (e.key === 'Escape') setAddingModel(false) }}
                autoFocus
              />
              <button className="inv-btn inv-btn-sm" onClick={handleAddModel}><Plus size={14} /></button>
              <button className="inv-btn inv-btn-sm" onClick={() => { setAddingModel(false); setNewModelName('') }}><X size={14} /></button>
            </div>
          ) : (
            <button className="inv-model-btn inv-model-add-btn" onClick={() => setAddingModel(true)} title="Agregar modelo">
              <Plus size={14} />
            </button>
          )}
        </div>
        <div className="inv-toolbar-right">
          <button className="inv-btn inv-btn-primary" onClick={() => {
            setShowForm(!showForm)
            setFormData({ quantity: 1 })
            setAddMode('component')
          }}>
            <Plus size={16} /> Agregar al BOM
          </button>
        </div>
      </div>

      {/* BOM Summary */}
      <div className="inv-bom-summary">
        <div className="inv-bom-stat">
          <span className="inv-bom-stat-label">Costo por unidad</span>
          {hasRange ? (
            <span className="inv-bom-stat-value inv-bom-range">
              {formatCurrency(bomMinCost)} <span className="inv-bom-range-sep">—</span> {formatCurrency(bomMaxCost)}
            </span>
          ) : (
            <span className="inv-bom-stat-value">{formatCurrency(bomMinCost)}</span>
          )}
        </div>
        <div className="inv-bom-stat">
          <span className="inv-bom-stat-label">Pueden armarse</span>
          <span className={`inv-bom-stat-value ${canBuild === 0 ? 'zero' : ''}`}>{canBuild}</span>
        </div>
        <div className="inv-bom-stat">
          <span className="inv-bom-stat-label">Componentes</span>
          <span className="inv-bom-stat-value">{bom.length}</span>
        </div>
        <div className="inv-bom-stat inv-bom-price-stat">
          <span className="inv-bom-stat-label">Precio de Venta</span>
          {editingPrice ? (
            <div className="inv-bom-price-edit">
              <input
                type="number"
                min="0"
                step="100"
                value={editPrice}
                onChange={e => setEditPrice(e.target.value)}
                onBlur={async () => {
                  const val = Math.max(0, Number(editPrice) || 0)
                  await onUpdateModelPrice(bomModel, val)
                  setEditingPrice(false)
                }}
                onKeyDown={async e => {
                  if (e.key === 'Enter') {
                    e.target.blur()
                  } else if (e.key === 'Escape') {
                    setEditingPrice(false)
                    setEditPrice('')
                  }
                }}
                autoFocus
              />
            </div>
          ) : (
            <span
              className="inv-bom-stat-value inv-bom-price-display"
              onClick={() => { setEditPrice(modelPrices?.[bomModel] || 0); setEditingPrice(true) }}
              title="Click para editar"
            >
              {modelPrices?.[bomModel] > 0 ? formatCurrency(modelPrices[bomModel]) : <span className="inv-price-unset">— Establecer</span>}
              <Pencil size={12} className="inv-price-edit-icon" />
            </span>
          )}
        </div>
      </div>

      {/* Add to BOM Form */}
      {showForm && (
        <div className="inv-form-card">
          {/* Mode toggle */}
          <div className="inv-add-mode-toggle">
            <button
              className={`inv-mode-btn ${addMode === 'component' ? 'active' : ''}`}
              onClick={() => { setAddMode('component'); setFormData({ quantity: 1 }) }}
            >
              Componente directo
            </button>
            <button
              className={`inv-mode-btn ${addMode === 'subbom' ? 'active' : ''}`}
              onClick={() => { setAddMode('subbom'); setFormData({ quantity: 1, variantIds: [] }) }}
            >
              <Layers size={14} /> Sub-BOM
            </button>
          </div>

          {addMode === 'component' ? (
            <>
              <h4>Agregar componente al BOM de {bomModel}</h4>
              <div className="inv-form-grid">
                <div className="inv-form-group">
                  <label>Componente *</label>
                  <select value={formData.componentId || ''} onChange={e => setFormData(p => ({ ...p, componentId: e.target.value }))}>
                    <option value="">Seleccionar...</option>
                    {components.map(c => <option key={c.$id} value={c.$id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="inv-form-group">
                  <label>Cantidad por dispositivo *</label>
                  <input type="number" min="1" value={formData.quantity || 1}
                    onChange={e => setFormData(p => ({ ...p, quantity: e.target.value }))} />
                </div>
              </div>
            </>
          ) : (
            <>
              <h4>Agregar Sub-BOM al BOM de {bomModel}</h4>
              {subBoms.length === 0 ? (
                <div className="inv-empty-notice">
                  No hay Sub-BOMs creados aún. Abrí el panel <strong>Sub-BOMs</strong> para crear uno.
                </div>
              ) : (
                <div className="inv-form-grid">
                  <div className="inv-form-group">
                    <label>Sub-BOM principal *</label>
                    <select
                      value={formData.subBomId || ''}
                      onChange={e => setFormData(p => ({ ...p, subBomId: e.target.value }))}
                    >
                      <option value="">Seleccionar...</option>
                      {subBoms.map(sb => (
                        <option key={sb.$id} value={sb.$id}>
                          {sb.name} ({sb.components?.length || 0} comp. — {formatCurrency(calcSubBomCost(sb.$id))})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="inv-form-group">
                    <label>Cantidad mínima *</label>
                    <input type="number" min="1" value={formData.quantity || 1}
                      onChange={e => setFormData(p => ({ ...p, quantity: e.target.value }))} />
                  </div>
                  <div className="inv-form-group">
                    <label>
                      Cantidad máxima
                      <span className="inv-label-hint"> — opcional, habilita rango de cantidad</span>
                    </label>
                    <input
                      type="number"
                      min={formData.quantity || 1}
                      value={formData.maxQuantity || ''}
                      placeholder={`${formData.quantity || 1} (igual a mín)`}
                      onChange={e => setFormData(p => ({ ...p, maxQuantity: e.target.value || undefined }))}
                    />
                  </div>
                  {subBoms.length > 1 && formData.subBomId && (
                    <div className="inv-form-group inv-form-full">
                      <label>
                        Variantes alternativas
                        <span className="inv-label-hint"> — habilitan rango de precios</span>
                      </label>
                      <div className="inv-variant-checkboxes">
                        {subBoms.filter(sb => sb.$id !== formData.subBomId).map(sb => (
                          <label key={sb.$id} className="inv-variant-checkbox-label">
                            <input
                              type="checkbox"
                              checked={(formData.variantIds || []).includes(sb.$id)}
                              onChange={e => {
                                const cur = formData.variantIds || []
                                setFormData(p => ({
                                  ...p,
                                  variantIds: e.target.checked
                                    ? [...cur, sb.$id]
                                    : cur.filter(id => id !== sb.$id)
                                }))
                              }}
                            />
                            <span>{sb.name}</span>
                            <span className="inv-variant-cost">{formatCurrency(calcSubBomCost(sb.$id))}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="inv-form-actions">
            <button className="inv-btn" onClick={() => { setShowForm(false); setFormData({}) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="inv-btn inv-btn-primary" onClick={onCreate} disabled={processing}>
              <Save size={14} /> Agregar
            </button>
          </div>
        </div>
      )}

      {/* BOM Table */}
      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Componente</th>
              <th>Cantidad</th>
              <th>Stock Actual</th>
              <th>Costo Unitario (avg)</th>
              <th>Costo Línea</th>
              <th>Alcanza para</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {bom.length === 0 ? (
              <tr><td colSpan={7} className="inv-empty-cell">Sin componentes en el BOM de {bomModel}.</td></tr>
            ) : (
              bom.map(b => {
                if (b.subBomId) {
                  const allVariants = [b.subBomId, ...(b.variantIds || [])]
                  const unitCosts = allVariants.map(vid => calcSubBomCost(vid))
                  const minUnit = Math.min(...unitCosts)
                  const maxUnit = Math.max(...unitCosts)
                  const minQty = b.quantity || 1
                  const maxQty = b.maxQuantity && b.maxQuantity > minQty ? b.maxQuantity : minQty
                  const hasVariantRange = minUnit !== maxUnit
                  const hasQtyRange = maxQty > minQty
                  const isRange = hasVariantRange || hasQtyRange
                  const primarySb = subBomsMap[b.subBomId]
                  const builds = calcSubBomBuilds(b.subBomId)
                  const lineCostMin = minUnit * minQty
                  const lineCostMax = maxUnit * maxQty
                  return (
                    <tr key={b.$id} className="inv-subbom-row">
                      <td className="inv-name-cell">
                        <span className="inv-subbom-badge"><Layers size={11} /> Sub-BOM</span>
                        <span className="inv-subbom-row-name">{primarySb?.name || b.subBomId}</span>
                        {allVariants.length > 1 && (
                          <div className="inv-variant-tags">
                            {allVariants.map(vid => (
                              <span key={vid} className={`inv-variant-tag ${vid === b.subBomId ? 'primary' : ''}`}>
                                {subBomsMap[vid]?.name || vid}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {hasQtyRange
                          ? <span className="inv-cost-range" style={{ fontSize: '0.8rem' }}>{minQty} — {maxQty}</span>
                          : minQty
                        }
                      </td>
                      <td>
                        <span className={`inv-stock-badge ${builds < minQty ? 'low' : 'ok'}`}>
                          {builds}
                        </span>
                      </td>
                      <td>
                        {hasVariantRange
                          ? <span className="inv-cost-range">{formatCurrency(minUnit)} — {formatCurrency(maxUnit)}</span>
                          : formatCurrency(minUnit)
                        }
                      </td>
                      <td>
                        {isRange
                          ? <span className="inv-cost-range">{formatCurrency(lineCostMin)} — {formatCurrency(lineCostMax)}</span>
                          : formatCurrency(lineCostMin)
                        }
                      </td>
                      <td>{builds > 0 ? Math.floor(builds / minQty) : 0}</td>
                      <td className="inv-actions-cell">
                        <button className="inv-icon-btn danger" onClick={() => onDelete(b.$id)}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  )
                }
                // Direct component row
                const summary = modelBomSummary.find(s => s.componentId === b.componentId)
                const comp = compMap[b.componentId]
                return (
                  <tr key={b.$id}>
                    <td className="inv-name-cell">{comp?.name || b.componentId}</td>
                    <td>{b.quantity}</td>
                    <td>
                      <span className={`inv-stock-badge ${(summary?.stock || 0) < b.quantity ? 'low' : 'ok'}`}>
                        {summary?.stock ?? comp?.currentStock ?? 0}
                      </span>
                    </td>
                    <td>{formatCurrency(summary?.avgCost || 0)}</td>
                    <td>{formatCurrency(summary?.lineCost || 0)}</td>
                    <td>{summary?.canBuild ?? '—'}</td>
                    <td className="inv-actions-cell">
                      <button className="inv-icon-btn danger" onClick={() => onDelete(b.$id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Sub-BOM Library — shown below the BOM table, always visible */}
      <div className="inv-subbom-library">
        <div className="inv-subbom-library-header">
          <div className="inv-subbom-library-title">
            <Layers size={15} />
            <span>Biblioteca de Sub-BOMs</span>
            <span className="inv-subbom-library-hint">módulos reutilizables · usados en cualquier BOM</span>
          </div>
          {!showNewSubBomForm && !editingSubBomId && (
            <button
              className="inv-btn inv-btn-sm inv-btn-primary"
              onClick={() => { setShowNewSubBomForm(true); setSubBomForm({ name: '', description: '', components: [] }) }}
            >
              <Plus size={13} /> Nuevo Sub-BOM
            </button>
          )}
        </div>

        {showNewSubBomForm && !editingSubBomId && (
          <div className="inv-subbom-create-section">
            <h4>Nuevo Sub-BOM</h4>
            <SubBomForm
              form={subBomForm}
              setForm={setSubBomForm}
              components={components}
              compMap={compMap}
              compAvgCostMap={compAvgCostMap}
              onSave={handleSaveSubBom}
              onCancel={() => { setShowNewSubBomForm(false); setSubBomForm({ name: '', description: '', components: [] }) }}
              processing={subBomProcessing}
            />
          </div>
        )}

        {subBoms.length === 0 && !showNewSubBomForm ? (
          <div className="inv-empty-notice">No hay Sub-BOMs creados todavía.</div>
        ) : (
          <div className="inv-subbom-library-grid">
            {subBoms.map(sb => {
              // Which models use this sub-BOM?
              const usedInModels = [...new Set(
                bom.filter(b => b.subBomId === sb.$id).map(b => b.deviceModel)
              )]
              return (
                <div key={sb.$id} className="inv-subbom-lib-card">
                  {editingSubBomId === sb.$id ? (
                    <>
                      <h4 className="inv-subbom-card-edit-title">Editando: {sb.name}</h4>
                      <SubBomForm
                        form={subBomForm}
                        setForm={setSubBomForm}
                        components={components}
                        compMap={compMap}
                        compAvgCostMap={compAvgCostMap}
                        onSave={handleSaveSubBom}
                        onCancel={() => { setEditingSubBomId(null); setSubBomForm({ name: '', description: '', components: [] }) }}
                        processing={subBomProcessing}
                        isEdit
                      />
                    </>
                  ) : (
                    <>
                      <div className="inv-subbom-lib-card-header">
                        <div className="inv-subbom-lib-info">
                          <span className="inv-subbom-lib-name">{sb.name}</span>
                          {sb.description && <span className="inv-subbom-card-desc">{sb.description}</span>}
                          {usedInModels.length > 0 && (
                            <div className="inv-subbom-used-in">
                              {usedInModels.map(m => (
                                <span key={m} className="inv-subbom-used-tag">{m}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="inv-subbom-lib-meta">
                          <span className="inv-subbom-lib-cost">{formatCurrency(calcSubBomCost(sb.$id))}/u</span>
                        </div>
                        <div className="inv-subbom-card-actions">
                          <button className="inv-icon-btn" title="Editar" onClick={() => {
                            setEditingSubBomId(sb.$id)
                            setShowNewSubBomForm(false)
                            setSubBomForm({ name: sb.name, description: sb.description || '', components: [...(sb.components || [])] })
                          }}>
                            <Edit3 size={14} />
                          </button>
                          <button className="inv-icon-btn danger" title="Eliminar" onClick={() => onDeleteSubBOM(sb.$id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="inv-subbom-components">
                        {(sb.components || []).map((c, i) => (
                          <span key={i} className="inv-subbom-comp-tag">
                            {compMap[c.componentId]?.name || c.componentId} ×{c.quantity}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}



// ═══════════════════════════════════════════════════════════
// TAB: ENTRADAS (Stock Purchases)
// ═══════════════════════════════════════════════════════════

function EntriesTab({
  entries, compMap, components, showForm, setShowForm,
  formData, setFormData, processing, onCreate, onDelete, priceTrends
}) {
  return (
    <>
      <div className="inv-toolbar">
        <span className="inv-toolbar-info">{entries.length} entradas de stock</span>
        <button className="inv-btn inv-btn-primary" onClick={() => {
          setShowForm(!showForm)
          setFormData({ quantity: 1, unitCost: 0 })
        }}>
          <Plus size={16} /> Nueva Entrada
        </button>
      </div>

      {/* Entry Form */}
      {showForm && (
        <div className="inv-form-card">
          <h4>Registrar Compra / Entrada de Stock</h4>
          <div className="inv-form-grid">
            <div className="inv-form-group">
              <label>Componente *</label>
              <select value={formData.componentId || ''} onChange={e => setFormData(p => ({ ...p, componentId: e.target.value }))}>
                <option value="">Seleccionar...</option>
                {components.map(c => <option key={c.$id} value={c.$id}>{c.name}</option>)}
              </select>
            </div>
            <div className="inv-form-group">
              <label>Cantidad *</label>
              <input type="number" min="1" value={formData.quantity || ''}
                onChange={e => setFormData(p => ({ ...p, quantity: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Precio Unitario *</label>
              <input type="number" min="0" step="0.01" value={formData.unitCost ?? ''}
                onChange={e => setFormData(p => ({ ...p, unitCost: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Proveedor</label>
              <input type="text" placeholder="Nombre del proveedor"
                value={formData.supplier || ''}
                onChange={e => setFormData(p => ({ ...p, supplier: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Referencia / Factura</label>
              <input type="text" placeholder="FAC-00123"
                value={formData.supplierRef || ''}
                onChange={e => setFormData(p => ({ ...p, supplierRef: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Fecha de Compra</label>
              <input type="date"
                value={formData.entryDate ? formData.entryDate.split('T')[0] : ''}
                onChange={e => setFormData(p => ({ ...p, entryDate: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
            </div>
            <div className="inv-form-group inv-form-full">
              <label>Notas</label>
              <input type="text" placeholder="Notas adicionales..."
                value={formData.notes || ''}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          {formData.quantity && formData.unitCost ? (
            <div className="inv-form-total">
              Total: {formatCurrency(Number(formData.quantity) * Number(formData.unitCost))}
            </div>
          ) : null}
          <div className="inv-form-actions">
            <button className="inv-btn" onClick={() => { setShowForm(false); setFormData({}) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="inv-btn inv-btn-primary" onClick={onCreate} disabled={processing}>
              <Save size={14} /> Registrar Entrada
            </button>
          </div>
        </div>
      )}

      {/* Entries Table */}
      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Componente</th>
              <th>Cantidad</th>
              <th>Restante</th>
              <th>Precio Unit.</th>
              <th>Total</th>
              <th>Proveedor</th>
              <th>Referencia</th>
              <th>Fecha</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={9} className="inv-empty-cell">Sin entradas de stock.</td></tr>
            ) : (
              entries.map(e => {
                const comp = compMap[e.componentId]
                // Find previous entry for same component to show trend
                const compEntries = priceTrends[e.componentId]?.entries || []
                const idx = compEntries.findIndex(ce => ce.$id === e.$id)
                const prevEntry = idx >= 0 && idx < compEntries.length - 1 ? compEntries[idx + 1] : null
                const priceDiff = prevEntry ? Number(e.unitCost) - Number(prevEntry.unitCost) : null
                const pricePct = prevEntry && Number(prevEntry.unitCost) > 0 ? (priceDiff / Number(prevEntry.unitCost)) * 100 : null
                return (
                  <tr key={e.$id} className={(e.remainingQty || 0) === 0 ? 'depleted-row' : ''}>
                    <td className="inv-name-cell">{comp?.name || e.componentId}</td>
                    <td>{e.quantity}</td>
                    <td>
                      <span className={`inv-stock-badge ${(e.remainingQty || 0) === 0 ? 'depleted' : 'ok'}`}>
                        {e.remainingQty ?? e.quantity}
                      </span>
                    </td>
                    <td>
                      <span className="inv-price-trend-cell">
                        <span>{formatCurrency(e.unitCost)}</span>
                        {priceDiff != null && priceDiff !== 0 && (
                          <span className={`inv-trend-badge ${priceDiff > 0 ? 'up' : 'down'}`} title={`Anterior: ${formatCurrency(prevEntry.unitCost)} (${priceDiff > 0 ? '+' : ''}${pricePct.toFixed(1)}%)`}>
                            {priceDiff > 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                            {priceDiff > 0 ? '+' : ''}{formatCurrency(Math.abs(priceDiff))}
                          </span>
                        )}
                      </span>
                    </td>
                    <td>{formatCurrency(e.totalCost)}</td>
                    <td>{e.supplier || '—'}</td>
                    <td>{e.supplierRef || '—'}</td>
                    <td>{formatDate(e.entryDate || e.$createdAt)}</td>
                    <td className="inv-actions-cell">
                      <button className="inv-icon-btn danger" title="Eliminar" onClick={() => onDelete(e.$id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: MOVIMIENTOS
// ═══════════════════════════════════════════════════════════

const MANUAL_REASONS = [
  { value: 'warranty', label: 'Garantía' },
  { value: 'damage', label: 'Daño / Rotura' },
  { value: 'loss', label: 'Pérdida / Extravío' },
  { value: 'return_stock', label: 'Devolución a stock' },
  { value: 'internal_use', label: 'Uso interno / Testing' },
  { value: 'correction', label: 'Corrección de stock' },
  { value: 'other', label: 'Otro' },
]

function MovementsTab({ movements, compMap, components, DEVICE_MODELS, onManualMovement, onDeleteMovement }) {
  const [showManualModal, setShowManualModal] = useState(false)
  const [manualForm, setManualForm] = useState({ componentId: '', quantity: -1, reason: 'warranty', notes: '', deviceModel: '', deviceMac: '' })
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!manualForm.componentId || !manualForm.quantity || !manualForm.reason) return
    setSubmitting(true)
    try {
      await onManualMovement(manualForm)
      setShowManualModal(false)
      setManualForm({ componentId: '', quantity: -1, reason: 'warranty', notes: '', deviceModel: '', deviceMac: '' })
    } catch (e) {
      console.error('Manual movement error:', e)
      alert('Error al registrar movimiento: ' + (e.message || 'Error desconocido'))
    } finally {
      setSubmitting(false)
    }
  }

  const selectedComp = compMap[manualForm.componentId]

  return (
    <>
      <div className="inv-toolbar">
        <span className="inv-toolbar-info">{movements.length} movimientos registrados</span>
        <button className="inv-btn inv-btn-primary" onClick={() => setShowManualModal(true)}>
          <Plus size={16} /> Registrar Movimiento
        </button>
      </div>

      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Componente</th>
              <th>Cantidad</th>
              <th>Costo Unit.</th>
              <th>Costo Total</th>
              <th>Modelo</th>
              <th>MAC</th>
              <th>Razón</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {movements.length === 0 ? (
              <tr><td colSpan={10} className="inv-empty-cell">Sin movimientos registrados.</td></tr>
            ) : (
              movements.map(m => {
                const comp = compMap[m.componentId]
                return (
                  <tr key={m.$id}>
                    <td>{formatDate(m.movementDate || m.$createdAt)}</td>
                    <td>
                      <span className={`inv-type-badge ${m.type}`}>
                        {m.type === 'deduction' ? '📤 Salida'
                          : m.type === 'manual_out' ? '📤 Salida manual'
                            : m.type === 'manual_in' ? '📥 Ingreso manual'
                              : m.type === 'adjustment' ? '🔧 Ajuste'
                                : '📥 Retorno'}
                      </span>
                    </td>
                    <td className="inv-name-cell">{comp?.name || m.componentId}</td>
                    <td className={m.quantity < 0 ? 'neg' : 'pos'}>{m.quantity}</td>
                    <td>{formatCurrency(m.unitCost)}</td>
                    <td>{formatCurrency(m.totalCost)}</td>
                    <td>{m.deviceModel ? <span className="inv-model-badge">{m.deviceModel}</span> : '—'}</td>
                    <td className="inv-mac-cell">{m.deviceMac || '—'}</td>
                    <td>{m.notes ? `${MANUAL_REASONS.find(r => r.value === m.reason)?.label || m.reason} — ${m.notes}` : (MANUAL_REASONS.find(r => r.value === m.reason)?.label || m.reason || '—')}</td>
                    <td>
                      <button
                        className="inv-btn inv-btn-danger inv-btn-sm"
                        title="Eliminar movimiento"
                        onClick={async () => {
                          if (window.confirm('¿Eliminar este movimiento? Esta acción no afecta el stock actual.')) {
                            try {
                              await onDeleteMovement(m.$id)
                            } catch (e) {
                              alert('Error al eliminar movimiento: ' + (e.message || 'Error desconocido'))
                            }
                          }
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {showManualModal && (
        <div className="inv-modal-overlay" onClick={() => setShowManualModal(false)}>
          <div className="inv-modal" onClick={e => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h3><History size={20} /> Registrar Movimiento Manual</h3>
              <button onClick={() => setShowManualModal(false)}><X size={18} /></button>
            </div>
            <div className="inv-modal-body">
              <p className="inv-modal-desc">Registrá una salida o ingreso manual de componentes. Las salidas descuentan stock a costo FIFO.</p>

              <div className="inv-form-group">
                <label>Componente *</label>
                <select value={manualForm.componentId} onChange={e => setManualForm(p => ({ ...p, componentId: e.target.value }))}>
                  <option value="">— Seleccionar componente —</option>
                  {components.map(c => (
                    <option key={c.$id} value={c.$id}>{c.name} (stock: {c.currentStock || 0})</option>
                  ))}
                </select>
              </div>

              <div className="inv-form-group">
                <label>Cantidad * (negativo = salida, positivo = ingreso)</label>
                <input
                  type="number"
                  value={manualForm.quantity}
                  onChange={e => setManualForm(p => ({ ...p, quantity: Number(e.target.value) }))}
                />
                {manualForm.quantity < 0 && selectedComp && (
                  <small style={{ color: (selectedComp.currentStock || 0) >= Math.abs(manualForm.quantity) ? '#4ade80' : '#f87171' }}>
                    Stock actual: {selectedComp.currentStock || 0} — Se descontarán {Math.abs(manualForm.quantity)} unidad(es)
                  </small>
                )}
              </div>

              <div className="inv-form-group">
                <label>Razón *</label>
                <select value={manualForm.reason} onChange={e => setManualForm(p => ({ ...p, reason: e.target.value }))}>
                  {MANUAL_REASONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="inv-form-group">
                <label>Notas (opcional)</label>
                <input
                  type="text"
                  placeholder="Ej: Sensor fallado, reemplazo garantía cliente X"
                  value={manualForm.notes}
                  onChange={e => setManualForm(p => ({ ...p, notes: e.target.value }))}
                />
              </div>

              <div className="inv-form-group">
                <label>Modelo de dispositivo (opcional)</label>
                <select value={manualForm.deviceModel} onChange={e => setManualForm(p => ({ ...p, deviceModel: e.target.value }))}>
                  <option value="">— Sin modelo —</option>
                  {DEVICE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div className="inv-form-group">
                <label>MAC del dispositivo (opcional)</label>
                <input
                  type="text"
                  placeholder="XX:XX:XX:XX:XX:XX"
                  value={manualForm.deviceMac}
                  onChange={e => setManualForm(p => ({ ...p, deviceMac: e.target.value }))}
                />
              </div>
            </div>
            <div className="inv-modal-footer">
              <button className="inv-btn" onClick={() => setShowManualModal(false)}>Cancelar</button>
              <button
                className={`inv-btn ${manualForm.quantity < 0 ? 'inv-btn-danger' : 'inv-btn-primary'}`}
                onClick={handleSubmit}
                disabled={submitting || !manualForm.componentId || !manualForm.quantity || !manualForm.reason}
              >
                {submitting ? 'Procesando...' : manualForm.quantity < 0 ? 'Registrar Salida' : 'Registrar Ingreso'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: PRODUCTOS (Retail / Grow supplies)
// ═══════════════════════════════════════════════════════════

function ProductsTab({
  products, searchTerm, setSearchTerm, showForm, setShowForm,
  editingId, setEditingId, formData, setFormData, processing,
  onCreate, onUpdate, onDelete
}) {
  return (
    <>
      <div className="inv-toolbar">
        <div className="inv-search-wrap">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="inv-btn inv-btn-primary" onClick={() => {
          setShowForm(!showForm)
          setEditingId(null)
          setFormData({ category: 'otro', unit: 'unidad', taxCategory: 'exento', active: true, stock: 0, minStock: 0 })
        }}>
          <Plus size={16} /> Nuevo Producto
        </button>
      </div>

      {(showForm || editingId) && (
        <div className="inv-form-card">
          <h4>{editingId ? 'Editar Producto' : 'Nuevo Producto'}</h4>
          <p style={{ margin: '-4px 0 12px', fontSize: '0.82em', color: 'var(--text-muted)' }}>Para dispositivos, el <strong>SKU</strong> debe coincidir con el modelo (ej: GP-BOX, GP-MINI). El precio de venta se usa en facturación.</p>
          <div className="inv-form-grid">
            <div className="inv-form-group">
              <label>Nombre *</label>
              <input type="text" placeholder="Bomba sumergible 25W" value={formData.name || ''}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>SKU (modelo)</label>
              <input type="text" placeholder="GP-BOX" value={formData.sku || ''}
                onChange={e => setFormData(p => ({ ...p, sku: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Categoría</label>
              <select value={formData.category || 'otro'}
                onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}>
                {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="inv-form-group">
              <label>Precio de Costo</label>
              <input type="number" min="0" step="0.01" value={formData.costPrice ?? ''}
                onChange={e => setFormData(p => ({ ...p, costPrice: Number(e.target.value) }))} />
            </div>
            <div className="inv-form-group">
              <label>Precio de Venta *</label>
              <input type="number" min="0" step="0.01" value={formData.salePrice ?? ''}
                onChange={e => setFormData(p => ({ ...p, salePrice: Number(e.target.value) }))} />
            </div>
            <div className="inv-form-group">
              <label>Stock</label>
              <input type="number" min="0" value={formData.stock ?? 0}
                onChange={e => setFormData(p => ({ ...p, stock: Number(e.target.value) }))} />
            </div>
            <div className="inv-form-group">
              <label>Stock Mínimo</label>
              <input type="number" min="0" value={formData.minStock ?? 0}
                onChange={e => setFormData(p => ({ ...p, minStock: Number(e.target.value) }))} />
            </div>
            <div className="inv-form-group">
              <label>Unidad</label>
              <input type="text" placeholder="unidad" value={formData.unit || ''}
                onChange={e => setFormData(p => ({ ...p, unit: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Categoría Fiscal</label>
              <select value={formData.taxCategory || 'exento'}
                onChange={e => setFormData(p => ({ ...p, taxCategory: e.target.value }))}>
                {TAX_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="inv-form-group inv-form-full">
              <label>Descripción</label>
              <input type="text" placeholder="Descripción del producto..."
                value={formData.description || ''}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          {formData.costPrice && formData.salePrice ? (
            <div className="inv-form-total">
              Margen: {formatCurrency(formData.salePrice - formData.costPrice)} ({Math.round(((formData.salePrice - formData.costPrice) / formData.salePrice) * 100)}%)
            </div>
          ) : null}
          <div className="inv-form-actions">
            <button className="inv-btn" onClick={() => { setShowForm(false); setEditingId(null); setFormData({}) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="inv-btn inv-btn-primary" onClick={editingId ? onUpdate : onCreate} disabled={processing}>
              <Save size={14} /> {editingId ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>SKU</th>
              <th>Categoría</th>
              <th>Costo</th>
              <th>Precio</th>
              <th>Margen</th>
              <th>Stock</th>
              <th>Fiscal</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr><td colSpan={9} className="inv-empty-cell">No hay productos configurados. Creá uno por cada dispositivo o artículo que vendas, con su precio de venta.</td></tr>
            ) : (
              products.map(p => {
                const margin = p.salePrice && p.costPrice ? Math.round(((p.salePrice - p.costPrice) / p.salePrice) * 100) : null
                return (
                  <tr key={p.$id} className={`${(p.stock || 0) <= (p.minStock || 0) ? 'low-stock-row' : ''} ${!p.active ? 'depleted-row' : ''}`}>
                    <td className="inv-name-cell">{p.name}</td>
                    <td>{p.sku || '—'}</td>
                    <td><span className="inv-cat-badge">{p.category}</span></td>
                    <td>{formatCurrency(p.costPrice)}</td>
                    <td className="inv-price-cell">{formatCurrency(p.salePrice)}</td>
                    <td>{margin != null ? <span className={`inv-margin ${margin >= 30 ? 'good' : 'low'}`}>{margin}%</span> : '—'}</td>
                    <td>
                      <span className={`inv-stock-badge ${(p.stock || 0) <= (p.minStock || 0) ? 'low' : 'ok'}`}>
                        {p.stock || 0}
                      </span>
                    </td>
                    <td>{p.taxCategory || 'exento'}</td>
                    <td className="inv-actions-cell">
                      <button className="inv-icon-btn" title="Editar" onClick={() => {
                        setEditingId(p.$id)
                        setShowForm(false)
                        setFormData({
                          name: p.name, sku: p.sku, category: p.category, description: p.description,
                          costPrice: p.costPrice, salePrice: p.salePrice, stock: p.stock,
                          minStock: p.minStock, unit: p.unit, taxCategory: p.taxCategory, active: p.active,
                        })
                      }}>
                        <Edit3 size={14} />
                      </button>
                      <button className="inv-icon-btn danger" title="Eliminar" onClick={() => onDelete(p.$id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// SUPPLIERS TAB
// ═══════════════════════════════════════════════════════════

function SuppliersTab({
  suppliers, searchTerm, setSearchTerm,
  showForm, setShowForm, editingId, setEditingId,
  formData, setFormData, processing,
  onCreate, onUpdate, onDelete,
}) {
  const activeCount = suppliers.filter(s => s.active !== false).length

  return (
    <>
      {/* KPIs */}
      <div className="inv-kpis">
        <div className="inv-kpi">
          <Truck size={20} />
          <div>
            <span className="inv-kpi-value">{suppliers.length}</span>
            <span className="inv-kpi-label">Total Proveedores</span>
          </div>
        </div>
        <div className="inv-kpi">
          <CheckCircle size={20} />
          <div>
            <span className="inv-kpi-value">{activeCount}</span>
            <span className="inv-kpi-label">Activos</span>
          </div>
        </div>
      </div>

      {/* Search + Add */}
      <div className="inv-toolbar">
        <div className="inv-search-wrap">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar proveedor..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="inv-btn inv-btn-primary" onClick={() => {
          setShowForm(true)
          setEditingId(null)
          setFormData({ active: true, category: 'general' })
        }}>
          <Plus size={16} /> Nuevo Proveedor
        </button>
      </div>

      {/* Form */}
      {(showForm || editingId) && (
        <div className="inv-form-card">
          <h3>{editingId ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h3>
          <div className="inv-form-grid">
            <div className="inv-form-group">
              <label>Nombre *</label>
              <input type="text" value={formData.name || ''} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Contacto</label>
              <input type="text" value={formData.contactName || ''} onChange={e => setFormData(p => ({ ...p, contactName: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Email</label>
              <input type="email" value={formData.email || ''} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Teléfono</label>
              <input type="text" value={formData.phone || ''} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>CUIT</label>
              <input type="text" placeholder="XX-XXXXXXXX-X" value={formData.taxId || ''} onChange={e => setFormData(p => ({ ...p, taxId: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Categoría</label>
              <select value={formData.category || 'general'} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}>
                {SUPPLIER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="inv-form-group full-width">
              <label>Dirección</label>
              <input type="text" value={formData.address || ''} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="inv-form-group full-width">
              <label>Notas</label>
              <textarea rows={2} value={formData.notes || ''} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>
                <input type="checkbox" checked={formData.active !== false} onChange={e => setFormData(p => ({ ...p, active: e.target.checked }))} />
                {' '}Activo
              </label>
            </div>
          </div>
          <div className="inv-form-actions">
            <button className="inv-btn" onClick={() => { setShowForm(false); setEditingId(null); setFormData({}) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="inv-btn inv-btn-primary" onClick={editingId ? onUpdate : onCreate} disabled={processing}>
              <Save size={14} /> {editingId ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Contacto</th>
              <th>Email</th>
              <th>Teléfono</th>
              <th>CUIT</th>
              <th>Categoría</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 ? (
              <tr><td colSpan={8} className="inv-empty">No hay proveedores registrados</td></tr>
            ) : (
              suppliers.map(s => (
                <tr key={s.$id} className={s.active === false ? 'depleted-row' : ''}>
                  <td className="inv-name-cell">{s.name}</td>
                  <td>{s.contactName || '—'}</td>
                  <td>{s.email || '—'}</td>
                  <td>{s.phone || '—'}</td>
                  <td>{s.taxId || '—'}</td>
                  <td><span className="inv-cat-badge">{s.category || 'general'}</span></td>
                  <td>
                    <span className={`inv-stock-badge ${s.active !== false ? 'ok' : 'low'}`}>
                      {s.active !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="inv-actions-cell">
                    <button className="inv-icon-btn" title="Editar" onClick={() => {
                      setEditingId(s.$id)
                      setShowForm(false)
                      setFormData({
                        name: s.name, contactName: s.contactName, email: s.email,
                        phone: s.phone, address: s.address, taxId: s.taxId,
                        category: s.category, notes: s.notes, active: s.active,
                      })
                    }}>
                      <Edit3 size={14} />
                    </button>
                    <button className="inv-icon-btn danger" title="Eliminar" onClick={() => onDelete(s.$id)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// PURCHASE ORDERS TAB
// ═══════════════════════════════════════════════════════════

const PO_STATUS_LABELS = { draft: 'Borrador', sent: 'Enviada', received: 'Recibida', cancelled: 'Cancelada' }
const PO_STATUS_COLORS = { draft: 'draft', sent: 'sent', received: 'ok', cancelled: 'low' }

function PurchaseOrdersTab({
  purchaseOrders, suppliers, components,
  showForm, setShowForm, formData, setFormData,
  processing, onCreate, onSend, onReceive, onCancel, onDelete,
}) {
  const totalDraft = purchaseOrders.filter(p => p.status === 'draft').length
  const totalSent = purchaseOrders.filter(p => p.status === 'sent').length
  const totalReceived = purchaseOrders.filter(p => p.status === 'received').length
  const totalAmount = purchaseOrders.filter(p => p.status !== 'cancelled').reduce((s, p) => s + (p.totalAmount || 0), 0)

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...(prev.items || []), { componentId: '', componentName: '', quantity: 1, unitCost: 0 }],
    }))
  }

  const updateItem = (idx, field, value) => {
    setFormData(prev => {
      const items = [...(prev.items || [])]
      items[idx] = { ...items[idx], [field]: value }
      if (field === 'componentId') {
        const comp = components.find(c => c.$id === value)
        if (comp) items[idx].componentName = comp.name
      }
      return { ...prev, items }
    })
  }

  const removeItem = (idx) => {
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).filter((_, i) => i !== idx),
    }))
  }

  const activeSuppliers = suppliers.filter(s => s.active !== false)

  return (
    <>
      {/* KPIs */}
      <div className="inv-kpis">
        <div className="inv-kpi">
          <ClipboardList size={20} />
          <div>
            <span className="inv-kpi-value">{purchaseOrders.length}</span>
            <span className="inv-kpi-label">Total OC</span>
          </div>
        </div>
        <div className="inv-kpi">
          <Edit3 size={20} />
          <div>
            <span className="inv-kpi-value">{totalDraft}</span>
            <span className="inv-kpi-label">Borrador</span>
          </div>
        </div>
        <div className="inv-kpi">
          <ArrowUpCircle size={20} />
          <div>
            <span className="inv-kpi-value">{totalSent}</span>
            <span className="inv-kpi-label">Enviadas</span>
          </div>
        </div>
        <div className="inv-kpi">
          <CheckCircle size={20} />
          <div>
            <span className="inv-kpi-value">{totalReceived}</span>
            <span className="inv-kpi-label">Recibidas</span>
          </div>
        </div>
        <div className="inv-kpi">
          <DollarSign size={20} />
          <div>
            <span className="inv-kpi-value">{formatCurrency(totalAmount)}</span>
            <span className="inv-kpi-label">Monto Total</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="inv-toolbar">
        <div />
        <button className="inv-btn inv-btn-primary" onClick={() => {
          setShowForm(true)
          setFormData({ items: [], issueDate: new Date().toISOString().split('T')[0] })
        }}>
          <Plus size={16} /> Nueva Orden de Compra
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="inv-form-card">
          <h3>Nueva Orden de Compra</h3>
          <div className="inv-form-grid">
            <div className="inv-form-group">
              <label>Proveedor *</label>
              <select
                value={formData.supplierId || ''}
                onChange={e => {
                  const sup = activeSuppliers.find(s => s.$id === e.target.value)
                  setFormData(p => ({ ...p, supplierId: e.target.value, supplierName: sup?.name || '' }))
                }}
              >
                <option value="">Seleccionar...</option>
                {activeSuppliers.map(s => <option key={s.$id} value={s.$id}>{s.name}</option>)}
              </select>
            </div>
            <div className="inv-form-group">
              <label>Fecha</label>
              <input type="date" value={formData.issueDate || ''} onChange={e => setFormData(p => ({ ...p, issueDate: e.target.value }))} />
            </div>
            <div className="inv-form-group">
              <label>Fecha esperada entrega</label>
              <input type="date" value={formData.expectedDate || ''} onChange={e => setFormData(p => ({ ...p, expectedDate: e.target.value }))} />
            </div>
            <div className="inv-form-group full-width">
              <label>Notas</label>
              <input type="text" value={formData.notes || ''} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>

          {/* Items */}
          <h4 style={{ marginTop: '1rem' }}>Items</h4>
          <div className="inv-table-wrap">
            <table className="inv-table">
              <thead>
                <tr>
                  <th>Componente</th>
                  <th>Cantidad</th>
                  <th>Costo Unit.</th>
                  <th>Subtotal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(formData.items || []).map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <select value={item.componentId} onChange={e => updateItem(idx, 'componentId', e.target.value)}>
                        <option value="">Seleccionar...</option>
                        {components.map(c => <option key={c.$id} value={c.$id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td><input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', Number(e.target.value))} style={{ width: 80 }} /></td>
                    <td><input type="number" min="0" step="0.01" value={item.unitCost} onChange={e => updateItem(idx, 'unitCost', Number(e.target.value))} style={{ width: 100 }} /></td>
                    <td>{formatCurrency(item.quantity * item.unitCost)}</td>
                    <td><button className="inv-icon-btn danger" onClick={() => removeItem(idx)}><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="inv-btn" onClick={addItem} style={{ marginTop: '0.5rem' }}>
            <Plus size={14} /> Agregar Item
          </button>
          <div style={{ textAlign: 'right', fontWeight: 600, marginTop: '0.5rem' }}>
            Total: {formatCurrency((formData.items || []).reduce((s, it) => s + it.quantity * it.unitCost, 0))}
          </div>

          <div className="inv-form-actions">
            <button className="inv-btn" onClick={() => { setShowForm(false); setFormData({ items: [] }) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="inv-btn inv-btn-primary" onClick={onCreate} disabled={processing || !formData.supplierId || (formData.items || []).length === 0}>
              <Save size={14} /> Crear OC
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Nº OC</th>
              <th>Proveedor</th>
              <th>Estado</th>
              <th>Items</th>
              <th>Total</th>
              <th>Fecha</th>
              <th>Entrega Esp.</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {purchaseOrders.length === 0 ? (
              <tr><td colSpan={8} className="inv-empty">No hay órdenes de compra</td></tr>
            ) : (
              purchaseOrders.map(po => {
                const items = typeof po.items === 'string' ? JSON.parse(po.items || '[]') : (po.items || [])
                return (
                  <tr key={po.$id}>
                    <td className="inv-name-cell">{po.poNumber}</td>
                    <td>{po.supplierName}</td>
                    <td>
                      <span className={`inv-stock-badge ${PO_STATUS_COLORS[po.status] || ''}`}>
                        {PO_STATUS_LABELS[po.status] || po.status}
                      </span>
                    </td>
                    <td>{items.length}</td>
                    <td>{formatCurrency(po.totalAmount)}</td>
                    <td>{formatDate(po.issueDate)}</td>
                    <td>{formatDate(po.expectedDate)}</td>
                    <td className="inv-actions-cell">
                      {po.status === 'draft' && (
                        <button className="inv-icon-btn" title="Enviar" onClick={() => onSend(po.$id)}>
                          <ArrowUpCircle size={14} />
                        </button>
                      )}
                      {(po.status === 'draft' || po.status === 'sent') && (
                        <button className="inv-icon-btn" title="Recibir" onClick={() => onReceive(po.$id)} style={{ color: 'var(--success)' }}>
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {po.status !== 'received' && po.status !== 'cancelled' && (
                        <button className="inv-icon-btn danger" title="Cancelar" onClick={() => onCancel(po.$id)}>
                          <X size={14} />
                        </button>
                      )}
                      {po.status === 'draft' && (
                        <button className="inv-icon-btn danger" title="Eliminar" onClick={() => onDelete(po.$id)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: DISPONIBLE (Stock fabricado para la venta)
// ═══════════════════════════════════════════════════════════

const STATUS_LABELS_SI = { available: 'Disponible', reserved: 'Reservado', sold: 'Vendido' }
const STATUS_COLORS_SI = { available: '#4ade80', reserved: '#f59e0b', sold: '#94a3b8' }

function StockItemsTab({ stockItems, modelPrices, onDelete, onUpdateStatus, onBackfill }) {
  const [statusFilter, setStatusFilter] = useState('available')
  const [search, setSearch] = useState('')
  const [backfilling, setBackfilling] = useState(false)
  const [backfillMsg, setBackfillMsg] = useState(null)

  const handleBackfill = async () => {
    setBackfilling(true)
    setBackfillMsg(null)
    try {
      const res = await onBackfill()
      const count = res?.created ?? 0
      const skipped = res?.skipped ?? 0
      setBackfillMsg(count > 0
        ? `✔ ${count} unidad${count !== 1 ? 'es' : ''} registrada${count !== 1 ? 's' : ''} como disponibles${skipped > 0 ? ` (${skipped} ya existían)` : ''}.`
        : `Sin nuevas unidades. ${skipped > 0 ? `${skipped} ya estaban registradas.` : 'No hay movimientos de deducción pendientes.'}`)
    } catch (e) {
      setBackfillMsg('Error al registrar movimientos: ' + (e.message || e))
    } finally {
      setBackfilling(false)
    }
  }

  const filtered = stockItems.filter(si => {
    if (statusFilter && si.status !== statusFilter) return false
    if (search) {
      const s = search.toLowerCase()
      return (si.deviceModel || '').toLowerCase().includes(s) ||
        (si.mac || '').toLowerCase().includes(s) ||
        (si.invoiceNumber || '').toLowerCase().includes(s)
    }
    return true
  })

  const cnt = (st) => stockItems.filter(s => s.status === st).length

  return (
    <>
      <div className="inv-toolbar">
        <div className="inv-search">
          <Search size={14} />
          <input placeholder="Buscar por modelo, MAC, factura..." value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { key: '', label: `Todos (${stockItems.length})` },
            { key: 'available', label: `Disponible (${cnt('available')})` },
            { key: 'reserved', label: `Reservado (${cnt('reserved')})` },
            { key: 'sold', label: `Vendido (${cnt('sold')})` },
          ].map(f => (
            <button key={f.key}
              className={`inv-btn inv-btn-sm ${statusFilter === f.key ? '' : ''}`}
              onClick={() => setStatusFilter(f.key)}
              style={statusFilter === f.key
                ? { background: 'var(--primary)', color: '#fff', border: '1px solid var(--primary)' }
                : {}}>
              {f.label}
            </button>
          ))}
          <button
            className="inv-btn inv-btn-sm"
            onClick={handleBackfill}
            disabled={backfilling}
            title="Registrar como disponibles los dispositivos deducidos antes de esta funcionalidad"
            style={{ display: 'flex', alignItems: 'center', gap: 5, opacity: backfilling ? 0.6 : 1 }}>
            <RefreshCw size={13} style={backfilling ? { animation: 'spin 1s linear infinite' } : {}} />
            {backfilling ? 'Registrando...' : 'Registrar movimientos anteriores'}
          </button>
        </div>
      </div>
      {backfillMsg && (
        <div style={{
          margin: '8px 0', padding: '8px 12px', borderRadius: 6, fontSize: '0.85em',
          background: backfillMsg.startsWith('✔') ? 'var(--success-bg, #d4edda)' : backfillMsg.startsWith('Error') ? 'var(--danger-bg, #f8d7da)' : 'var(--warning-bg, #fff3cd)',
          color: backfillMsg.startsWith('✔') ? 'var(--success, #155724)' : backfillMsg.startsWith('Error') ? 'var(--danger, #721c24)' : 'var(--warning-text, #856404)',
          border: '1px solid currentColor',
        }}>
          {backfillMsg}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="inv-empty">
          <Package2 size={32} />
          <p>{stockItems.length === 0
            ? 'No hay stock fabricado. Deducí un BOM para generar unidades disponibles para la venta.'
            : 'No hay ítems con ese filtro.'
          }</p>
        </div>
      ) : (
        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th>Modelo</th>
                <th>MAC / Serie</th>
                <th>Costo (COGS)</th>
                <th>Precio Venta</th>
                <th>Margen</th>
                <th>Estado</th>
                <th>Fabricado</th>
                <th>Factura</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(si => {
                const cost = si.productCost || 0
                const price = si.salePrice > 0 ? si.salePrice : (modelPrices?.[si.deviceModel] || 0)
                const margin = price > 0 ? Math.round(((price - cost) / price) * 100) : null
                return (
                  <tr key={si.$id}>
                    <td><strong>{si.deviceModel}</strong></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                      {si.mac || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td>{formatCurrency(cost)}</td>
                    <td>{price > 0 ? formatCurrency(price) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>{margin !== null
                      ? <span style={{ color: margin >= 0 ? 'var(--success)' : 'var(--danger)' }}>{margin}%</span>
                      : '—'}
                    </td>
                    <td>
                      <span style={{
                        background: STATUS_COLORS_SI[si.status] + '22',
                        color: STATUS_COLORS_SI[si.status],
                        border: `1px solid ${STATUS_COLORS_SI[si.status]}44`,
                        padding: '2px 8px', borderRadius: 4, fontSize: '0.82em', whiteSpace: 'nowrap'
                      }}>
                        {STATUS_LABELS_SI[si.status] || si.status}
                      </span>
                    </td>
                    <td>{formatDate(si.fabricatedAt)}</td>
                    <td style={{ fontSize: '0.85em' }}>
                      {si.invoiceNumber
                        ? <span style={{ color: 'var(--primary)' }}>{si.invoiceNumber}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>
                      }
                    </td>
                    <td className="inv-actions-cell">
                      {si.status === 'available' && (
                        <button className="inv-icon-btn danger" title="Eliminar" onClick={() => onDelete(si.$id)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// SUPPLIER SEARCH TAB
// ═══════════════════════════════════════════════════════════

// Default search aliases for common BOM component names
// Used as fallback when component.searchAlias is not set
const DEFAULT_SEARCH_ALIASES = {
  'ESP32': 'ESP32 WROOM devkit',
  'ESP32CAM': 'ESP32-CAM OV2640',
  'AHT10': 'AHT10',
  'ReleEx4': 'relay 4 canales 5V',
  'TOMA DOBLE': 'toma doble corriente',
  'TRANS_5V-2A_GPBOX': 'fuente 5V 2A USB',
  'TRANS_5V_2A_GENERAL': 'fuente 5V 2A USB',
  'INTERLOCK 10AMP HEMBRA': 'interlock 10A hembra',
  'CABLE INTERLOCK 3X1.5MM': 'cable interlock 3x1.5',
  'VARISTOR 14MM 275VAC': 'varistor 14mm 275V',
  'FUSIBLE 10A': 'fusible 10A vidrio',
  'MICROUSB 2M': 'cable micro USB 2m',
  'FICHA_CONECTOR_55x21': 'ficha DC 5.5x2.1',
  'FICHA_MICRO_USB_HEMBRA': 'conector micro USB hembra',
  'CON-EMPALME-X5': 'conector Wago 5 vias',
  'SENSOR T°AGUA': 'DS18B20 waterproof',
  'SENSOR TDS': 'sensor TDS agua',
  'SENSOR PH': 'sensor pH sonda',
  'CONVERSOR ANALOG_DIG': 'ADS1115',
  'Multiplexor x8': 'TCA9548A',
  'Mosfet_AO3401': 'AO3401 MOSFET',
  'R1/4-4.7k': 'resistencia 4.7K 1/4W',
  'R1/4-10k': 'resistencia 10K 1/4W',
  'R1/4-100k': 'resistencia 100K 1/4W',
  'TORNILLO_MUSB-HEMBRA': 'tornillo autoperforante',
  'TORNILLO_MUSB_HEMBRA': 'tornillo autoperforante',
  'TORNILLO_TAPA': 'tornillo autoperforante',
  'TORNILLO_RELAY_GPBOX': 'tornillo autoperforante',
}

// Components that should be excluded from supplier search (not purchasable)
const SKIP_SEARCH = ['IMPRESION3DGPBOX', 'IMPRESIONGPMINI']

function SupplierSearchTab({ components, bom, bomModel, compMap }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [selectedComponent, setSelectedComponent] = useState('')

  const bomComponents = useMemo(() => {
    return bom
      .map(b => {
        const comp = compMap[b.componentId]
        const name = comp?.name || b.componentId
        return {
          ...b,
          name,
          searchTerm: comp?.searchAlias || DEFAULT_SEARCH_ALIASES[name] || name,
          skip: SKIP_SEARCH.includes(name)
        }
      })
      .filter(b => !b.skip)
  }, [bom, compMap])

  const handleSearch = async (query) => {
    if (!query?.trim()) return
    setSearching(true)
    setSearchError(null)
    setResults(null)
    try {
      const data = await InventoryService.searchSupplierPrices(query.trim())
      setResults(data)
    } catch (err) {
      setSearchError(err.message || 'Error buscando precios')
    } finally {
      setSearching(false)
    }
  }

  const handleComponentSelect = (compId) => {
    setSelectedComponent(compId)
    const bomComp = bomComponents.find(b => b.componentId === compId)
    if (bomComp) {
      setSearchQuery(bomComp.searchTerm)
      handleSearch(bomComp.searchTerm)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    handleSearch(searchQuery)
  }

  return (
    <>
      <div className="inv-supplier-search">
        <form className="inv-ss-search-bar" onSubmit={handleSubmit}>
          <div className="inv-ss-input-wrap">
            <Search size={18} />
            <input
              type="text"
              placeholder="Buscar componente en proveedores argentinos..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <button type="submit" className="inv-btn inv-btn-primary" disabled={searching || !searchQuery.trim()}>
            {searching ? <><Loader size={16} className="inv-ss-spinner" /> Buscando...</> : <><Globe size={16} /> Buscar Precios</>}
          </button>
        </form>

        {bomComponents.length > 0 && (
          <div className="inv-ss-quick-search">
            <span className="inv-ss-quick-label">BOM {bomModel}:</span>
            <div className="inv-ss-quick-btns">
              {bomComponents.map(b => (
                <button
                  key={b.$id}
                  className={`inv-ss-quick-btn ${selectedComponent === b.componentId ? 'active' : ''}`}
                  onClick={() => handleComponentSelect(b.componentId)}
                >
                  {b.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {searchError && (
          <div className="inv-error">
            <AlertTriangle size={16} /> {searchError}
            <button onClick={() => setSearchError(null)}>×</button>
          </div>
        )}

        {results && (
          <div className="inv-ss-results">
            {results.cheapest?.length > 0 && (
              <div className="inv-ss-cheapest">
                <h3><TrendingDown size={18} /> Top 5 Más Baratos</h3>
                <div className="inv-ss-cheapest-grid">
                  {results.cheapest.map((item, i) => (
                    <a key={i} href={item.url} target="_blank" rel="noopener noreferrer" className="inv-ss-cheapest-card">
                      <div className="inv-ss-cheapest-rank">#{i + 1}</div>
                      <div className="inv-ss-cheapest-info">
                        <span className="inv-ss-cheapest-price">{formatCurrency(item.price)}</span>
                        <span className="inv-ss-cheapest-title">{item.title}</span>
                        <span className="inv-ss-cheapest-source">{item.sourceName}</span>
                      </div>
                      <ExternalLink size={14} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="inv-ss-sources">
              {results.sources.map(src => (
                <div key={src.sourceId} className="inv-ss-source-card">
                  <div className="inv-ss-source-header">
                    <h4>
                      {src.sourceName}
                      {src.error && <span className="inv-ss-source-error" title={src.error}>⚠</span>}
                    </h4>
                    <div className="inv-ss-source-meta">
                      {src.scraped && src.items.length > 0 && (
                        <span className="inv-ss-source-count">{src.count} resultado{src.count !== 1 ? 's' : ''}</span>
                      )}
                      <a href={src.searchUrl} target="_blank" rel="noopener noreferrer" className="inv-ss-source-link">
                        Ver en sitio <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>

                  {src.scraped && src.items.length > 0 ? (
                    <div className="inv-ss-items">
                      <table className="inv-table">
                        <thead>
                          <tr>
                            <th>Producto</th>
                            <th style={{ textAlign: 'right' }}>Precio</th>
                            <th style={{ width: 40 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {src.items.map((item, i) => (
                            <tr key={i}>
                              <td className="inv-ss-item-title">{item.title}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                {item.price ? formatCurrency(item.price) : item.priceText || '—'}
                              </td>
                              <td>
                                {item.url && (
                                  <a href={item.url} target="_blank" rel="noopener noreferrer" title="Ver producto">
                                    <ExternalLink size={14} />
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : src.scraped && !src.error ? (
                    <p className="inv-ss-no-results">Sin resultados para &ldquo;{results.query}&rdquo;</p>
                  ) : !src.scraped ? (
                    <p className="inv-ss-no-results inv-ss-manual">Búsqueda manual — click &ldquo;Ver en sitio&rdquo;</p>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="inv-ss-meta">
              Búsqueda: &ldquo;{results.query}&rdquo; · {results.totalResults} resultados con precio · {new Date(results.searchedAt).toLocaleString('es-AR')}
            </div>
          </div>
        )}

        {!results && !searching && !searchError && (
          <div className="inv-ss-empty">
            <Globe size={48} />
            <h3>Buscador de Precios en Proveedores</h3>
            <p>Buscá componentes del BOM en múltiples proveedores argentinos para encontrar los mejores precios.</p>
            <div className="inv-ss-sources-list">
              <strong>Fuentes con scraping automático:</strong>
              <span>TodoMicro · MercadoLibre · Nubbeo · Starware · InnoMake Bright</span>
              <strong>Fuentes con link directo:</strong>
              <span>Electrónica Argentina · Candy-Ho · Electrocomponentes · Elumiled · AliExpress</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
